import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import {
  Plus, Edit3, Trash2, Save, X, Upload, FolderOpen, Copy,
  Code, Mail, Heart, Eye, Monitor, Smartphone, UserPlus,
  GitBranch, CheckCircle2, AlertTriangle,
  Crosshair, Image, RefreshCw, Camera, MapPin,
  Layers, Settings, List, Play, Square, Loader, AlignJustify, FileText,
  Zap, Bot, StopCircle, ChevronDown, Sparkles
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */

const SCRIPT_TYPES = [
  { value: 'gmail', label: 'Gmail Creation', icon: Mail, color: '#ea4335', app: 'gmail' },
  { value: 'interact', label: 'Interact', icon: Heart, color: '#ec4899', app: 'instagram' },
  { value: 'scrape', label: 'Scrape', icon: Eye, color: '#6366f1', app: 'instagram' },
  { value: 'unfollow', label: 'Unfollow', icon: UserPlus, color: '#f59e0b', app: 'instagram' },
  { value: 'login', label: 'Login', icon: Smartphone, color: '#22c55e', app: 'instagram' },
  { value: 'setup', label: 'Account Setup', icon: Settings, color: '#8b5cf6', app: 'instagram' },
  { value: 'twitter-interact', label: 'Twitter Interact', icon: Heart, color: '#1d9bf0', app: 'twitter' },
  { value: 'twitter-scrape', label: 'Twitter Scrape', icon: Eye, color: '#1d9bf0', app: 'twitter' },
  { value: 'twitter-navigation', label: 'Twitter Navigation', icon: Monitor, color: '#000000', app: 'twitter' },
  { value: 'google-search', label: 'Google Search', icon: Eye, color: '#4285f4', app: 'google' },
  { value: 'tiktok-interact', label: 'TikTok Interact', icon: Play, color: '#000000', app: 'tiktok' },
  { value: 'tiktok-scrape', label: 'TikTok Scrape', icon: Eye, color: '#000000', app: 'tiktok' },
  { value: 'other', label: 'Other', icon: Code, color: '#64748b', app: 'other' },
  { value: 'ui-library', label: 'UI Library', icon: Image, color: '#a591ef', app: 'any' },
];

const APP_TABS = [
  { value: 'all', label: 'Tous', icon: Layers, color: 'var(--primary)' },
  { value: 'gmail', label: 'Gmail', icon: Mail, color: '#ea4335' },
  { value: 'instagram', label: 'Instagram', icon: Heart, color: '#ec4899' },
  { value: 'twitter', label: 'Twitter/X', icon: Monitor, color: '#1d9bf0' },
  { value: 'google', label: 'Google', icon: Eye, color: '#4285f4' },
  { value: 'tiktok', label: 'TikTok', icon: Play, color: '#000000' },
  { value: 'other', label: 'Autres', icon: Code, color: '#64748b' },
];

const ACTION_TYPES = [
  { value: 'action', label: 'Action', color: '#3b82f6' },
  { value: 'navigation', label: 'Navigation', color: '#8b5cf6' },
  { value: 'input', label: 'Input', color: '#22c55e' },
  { value: 'wait', label: 'Wait / Delay', color: '#f59e0b' },
  { value: 'check', label: 'Check / Assert', color: '#06b6d4' },
  { value: 'condition', label: 'Condition', color: '#ec4899' },
  { value: 'error', label: 'Error Handler', color: '#ef4444' },
  { value: 'loop', label: 'Loop', color: '#a855f7' },
];

const SELECTOR_TYPES = [
  { value: 'resource_id', label: 'Resource ID' },
  { value: 'text', label: 'Text' },
  { value: 'content_desc', label: 'Content Description' },
  { value: 'class_name', label: 'Class Name' },
  { value: 'xpath', label: 'XPath' },
  { value: 'uiautomator', label: 'UiAutomator' },
  { value: 'index', label: 'Index' },
  { value: 'bounds', label: 'Bounds' },
];

/* ═══════════════════════════════════════════════
   ANIMATIONS
   ═══════════════════════════════════════════════ */

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;
const slideIn = keyframes`from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
/* pulse available if needed */

const SpinnerIcon = styled.span`
  display: inline-flex; animation: ${spin} 1s linear infinite;
`;

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Container = styled.div`
  display: flex; height: 100%; background: var(--bg); overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px; min-width: 280px; background: var(--card-bg); border-right: 1px solid var(--card-border);
  display: flex; flex-direction: column; overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 12px 16px; border-bottom: 1px solid var(--card-border);
  display: flex; align-items: center; gap: 10px;
  h3 { margin: 0; font-size: 14px; color: var(--text-primary); font-weight: 700; }
`;

const AppTabBar = styled.div`
  display: flex; gap: 2px; padding: 8px 8px 0; border-bottom: 1px solid var(--card-border);
  flex-wrap: wrap;
`;

const AppTab = styled.button`
  display: flex; align-items: center; gap: 3px; padding: 5px 8px; border: none; cursor: pointer;
  font-size: 10px; font-weight: 600; border-radius: 6px 6px 0 0; transition: all .15s;
  white-space: nowrap;
  background: ${p => p.$active ? 'var(--card-bg)' : 'transparent'};
  color: ${p => p.$active ? p.$color || 'var(--primary)' : 'var(--text-muted)'};
  border-bottom: 2px solid ${p => p.$active ? p.$color || 'var(--primary)' : 'transparent'};
  &:hover { color: ${p => p.$color || 'var(--primary)'}; background: var(--surface-hover); }
`;

const VersionFilter = styled.div`
  padding: 6px 8px; border-bottom: 1px solid var(--card-border);
  display: flex; align-items: center; gap: 6px;
`;

const SidebarList = styled.div`
  flex: 1; overflow-y: auto; padding: 8px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 4px; }
`;

const FlowItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px;
  cursor: pointer; transition: all .15s; margin-bottom: 4px;
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  background: ${p => p.$active ? 'var(--primary)11' : 'transparent'};
  &:hover { background: ${p => p.$active ? 'var(--primary)11' : 'var(--surface-hover)'}; }
`;

const FlowIcon = styled.div`
  width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$color}18; color: ${p => p.$color}; flex-shrink: 0;
`;

const FlowMeta = styled.div`
  flex: 1; min-width: 0;
  .name { font-size: 13px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
`;

const MainArea = styled.div`
  flex: 1; display: flex; flex-direction: column; overflow: hidden;
`;

const MainHeader = styled.div`
  padding: 16px 24px; border-bottom: 1px solid var(--card-border); background: var(--card-bg);
  display: flex; align-items: center; gap: 16px; flex-shrink: 0;
`;

const FlowTitle = styled.div`
  flex: 1;
  h2 { margin: 0; font-size: 18px; font-weight: 700; color: var(--text-primary); display: flex; align-items: center; gap: 10px; }
  .version { font-size: 12px; color: var(--text-muted); font-weight: 400; margin-top: 2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
`;

const VersionSwitcher = styled.div`
  position: relative; display: inline-flex; align-items: center;
  .trigger {
    display: flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 5px;
    background: var(--surface-hover); border: 1px solid var(--card-border); cursor: pointer;
    font-size: 11px; font-weight: 600; color: var(--text-secondary); transition: all .15s;
    &:hover { border-color: var(--primary); color: var(--primary); }
  }
  .dropdown {
    position: absolute; top: calc(100% + 4px); left: 0; z-index: 100; min-width: 260px;
    background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,.4); overflow: hidden;
    max-height: 300px; overflow-y: auto;
  }
  .dropdown-item {
    display: flex; align-items: center; gap: 8px; padding: 8px 12px; cursor: pointer;
    font-size: 11px; transition: background .1s; border-bottom: 1px solid var(--card-border);
    &:last-child { border-bottom: none; }
    &:hover { background: var(--surface-hover); }
    &.active { background: var(--primary)11; border-left: 3px solid var(--primary); }
    .v-badge { padding: 1px 6px; border-radius: 4px; font-weight: 700; font-size: 10px;
      background: var(--surface-hover); color: var(--text-muted); white-space: nowrap; }
    .v-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-primary); }
    .v-steps { font-size: 10px; color: var(--text-muted); white-space: nowrap; }
  }
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px;
  font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; border: none;
  background: ${p => p.$primary ? 'var(--primary)' : p.$danger ? '#ef4444' : 'var(--surface-hover)'};
  color: ${p => (p.$primary || p.$danger) ? '#fff' : 'var(--text-secondary)'};
  &:hover { opacity: .85; transform: translateY(-1px); }
  &:disabled { opacity: .4; cursor: not-allowed; transform: none; }
`;

const FlowCanvas = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
  display: flex; gap: 0; align-items: flex-start;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 6px; }
`;

const StepColumn = styled.div`
  flex: 1; min-width: 0; max-width: 700px; margin: 0 auto; padding-right: 16px;
`;

const DetailPanel = styled.div`
  width: 380px; min-width: 380px; background: var(--card-bg); border-left: 1px solid var(--card-border);
  position: sticky; top: 0; align-self: flex-start;
  max-height: 100%; overflow-y: auto; animation: ${slideIn} .2s ease;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 4px; }
`;

const DetailHeader = styled.div`
  padding: 16px 20px; border-bottom: 1px solid var(--card-border); position: sticky; top: 0;
  background: var(--card-bg); z-index: 2; display: flex; align-items: center; gap: 10px;
  h3 { margin: 0; font-size: 14px; font-weight: 700; color: var(--text-primary); flex: 1; }
`;

const DetailBody = styled.div`padding: 16px 20px; display: flex; flex-direction: column; gap: 16px;`;

const FormGroup = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: .5px; }
`;

const Input = styled.input`
  padding: 8px 12px; border-radius: 6px; border: 1px solid var(--card-border);
  background: var(--bg); color: var(--text-primary); font-size: 13px;
  &:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 2px var(--primary)22; }
`;

const TextArea = styled.textarea`
  padding: 8px 12px; border-radius: 6px; border: 1px solid var(--card-border);
  background: var(--bg); color: var(--text-primary); font-size: 13px; resize: vertical; min-height: 60px;
  font-family: inherit;
  &:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 2px var(--primary)22; }
`;

const Select = styled.select`
  padding: 8px 12px; border-radius: 6px; border: 1px solid var(--card-border);
  background: var(--bg); color: var(--text-primary); font-size: 13px;
  &:focus { outline: none; border-color: var(--primary); }
`;

/* ─── Step Card ─── */

const StepConnector = styled.div`
  display: flex; flex-direction: column; align-items: center; padding: 6px 0;
  color: var(--text-muted); position: relative;
  &::before { content: ''; width: 2px; height: 100%; background: var(--card-border); position: absolute; top: 0; }
`;

const ConnectorDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%; background: var(--card-border); z-index: 1;
`;

const StepCard = styled.div`
  background: var(--card-bg); border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 12px; overflow: hidden; transition: all .2s; cursor: pointer;
  animation: ${fadeIn} .25s ease; position: relative;
  box-shadow: ${p => p.$active ? '0 0 0 2px var(--primary)33, 0 4px 20px rgba(0,0,0,.12)' : '0 2px 8px rgba(0,0,0,.06)'};
  &:hover { border-color: var(--primary); box-shadow: 0 4px 16px rgba(0,0,0,.1); transform: translateY(-1px); }
`;

const StepHeader = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 12px 16px;
  border-bottom: 1px solid var(--card-border);
`;

const StepNumber = styled.div`
  width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; background: ${p => p.$color}18; color: ${p => p.$color};
`;

const StepTitle = styled.div`
  flex: 1; min-width: 0;
  .title { font-size: 13px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .screen { font-size: 11px; color: var(--text-muted); margin-top: 1px; display: flex; align-items: center; gap: 4px; }
`;

const StepBadge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase;
  background: ${p => p.$color}18; color: ${p => p.$color};
`;

const StepBody = styled.div`
  padding: 12px 16px; display: flex; gap: 12px;
`;

const StepScreenshot = styled.div`
  width: 120px; height: 213px; border-radius: 8px; overflow: hidden; flex-shrink: 0;
  background: var(--surface-hover); border: 1px solid var(--card-border);
  display: flex; align-items: center; justify-content: center; position: relative;
  img { width: 100%; height: 100%; object-fit: cover; }
  .placeholder { color: var(--text-muted); display: flex; flex-direction: column; align-items: center; gap: 4px; font-size: 10px; }
`;

const StepInfo = styled.div`
  flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0;
`;

const StepDesc = styled.p`
  margin: 0; font-size: 12px; color: var(--text-secondary); line-height: 1.5;
`;

const SelectorList = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`;

const SelectorTag = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 4px 8px; border-radius: 6px;
  background: var(--bg); border: 1px solid var(--card-border); font-size: 11px;
  .type { font-weight: 700; color: ${p => p.$primary ? 'var(--primary)' : 'var(--text-muted)'}; text-transform: uppercase; font-size: 9px; letter-spacing: .5px; }
  .value { color: var(--text-primary); font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
  .fallback { color: var(--text-muted); font-style: italic; }
`;

const BranchLabel = styled.div`
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 12px;
  font-size: 10px; font-weight: 700; background: #ec489922; color: #ec4899; margin-bottom: 4px;
`;

const EmptyState = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 16px; color: var(--text-muted); padding: 60px 20px; text-align: center;
  h3 { margin: 0; font-size: 16px; color: var(--text-secondary); }
  p { margin: 0; font-size: 13px; max-width: 400px; line-height: 1.5; }
`;

/* ─── Create/Edit Flow Modal ─── */

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;

const Modal = styled.div`
  background: var(--card-bg); border-radius: 16px; width: 480px; max-height: 90vh; overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0,0,0,.3); animation: ${fadeIn} .2s ease;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px; display: flex; align-items: center; gap: 12px;
  h3 { margin: 0; font-size: 16px; font-weight: 700; color: var(--text-primary); flex: 1; }
`;

const ModalBody = styled.div`padding: 0 24px 24px; display: flex; flex-direction: column; gap: 14px;`;

const TypeGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
`;

const TypeCard = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 12px 8px;
  border-radius: 10px; cursor: pointer; transition: all .15s;
  border: 2px solid ${p => p.$active ? p.$color : 'var(--card-border)'};
  background: ${p => p.$active ? p.$color + '11' : 'var(--bg)'};
  .icon { color: ${p => p.$color}; }
  .label { font-size: 11px; font-weight: 600; color: var(--text-primary); text-align: center; }
  &:hover { border-color: ${p => p.$color}; }
`;

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--card-border);
`;

const SelectorCard = styled.div`
  padding: 8px 10px; background: var(--bg); border-radius: 8px;
  border: 1px solid var(--card-border); margin-bottom: 6px;
  animation: ${fadeIn} .2s ease;
`;

/* ─── Compact View ─── */

const CompactRow = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px;
  cursor: pointer; transition: all .15s; animation: ${fadeIn} .15s ease;
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  background: ${p => p.$active ? 'var(--primary)08' : 'var(--card-bg)'};
  &:hover { border-color: var(--primary); background: var(--primary)08; }
  margin-bottom: 4px;
`;

const CompactConnector = styled.div`
  width: 2px; height: 8px; background: var(--card-border); margin: 0 auto;
`;

const ToggleBtn = styled.button`
  display: flex; align-items: center; gap: 4px; padding: 5px 10px; border-radius: 6px;
  font-size: 11px; font-weight: 600; cursor: pointer; transition: all .15s; border: none;
  background: ${p => p.$active ? 'var(--primary)18' : 'var(--surface-hover)'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  &:hover { background: var(--primary)22; color: var(--primary); }
`;

/* ─── Decision Tree Branching ─── */

const BranchContainer = styled.div`
  display: flex; gap: 12px; margin-top: 4px; position: relative;
`;

const BranchArm = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center; position: relative;
  &::before {
    content: ''; width: 2px; height: 8px; background: var(--card-border);
  }
`;

const BranchTag = styled.div`
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px; margin-bottom: 4px;
  background: ${p => p.$color || '#ec4899'}22; color: ${p => p.$color || '#ec4899'};
  text-align: center; white-space: nowrap;
`;

/* ─── Test Runner ─── */

const TestBanner = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-radius: 8px;
  background: #22c55e12; border: 1px solid #22c55e33; margin-bottom: 12px;
  font-size: 12px; color: #22c55e; font-weight: 600;
  animation: ${fadeIn} .2s ease;
`;

const TestProgress = styled.div`
  height: 3px; background: var(--card-border); border-radius: 2px; overflow: hidden; margin-top: 4px;
  div { height: 100%; background: #22c55e; border-radius: 2px; transition: width .3s ease; }
`;

/* ─── Mapping Mode ─── */

const MappingPanel = styled.div`
  width: 380px; min-width: 380px; background: var(--card-bg); border-left: 1px solid var(--card-border);
  display: flex; flex-direction: column; position: sticky; top: 0; align-self: flex-start;
  max-height: 100%; overflow: hidden; animation: ${slideIn} .2s ease;
`;

const MappingHeader = styled.div`
  padding: 12px 16px; border-bottom: 1px solid var(--card-border);
  display: flex; align-items: center; gap: 10px; background: #6366f108;
  h3 { margin: 0; font-size: 14px; font-weight: 700; color: var(--text-primary); flex: 1;
    display: flex; align-items: center; gap: 8px; }
`;

const MirrorContainer = styled.div`
  width: 100%; aspect-ratio: 9/16; background: #000; border-bottom: 1px solid var(--card-border);
  display: flex; align-items: center; justify-content: center; overflow: hidden; position: relative;
  img { width: 100%; height: 100%; object-fit: contain; }
  .no-mirror { color: #555; text-align: center; font-size: 11px; display: flex; flex-direction: column;
    align-items: center; gap: 8px; padding: 20px; }
`;

const CaptureBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%;
  padding: 14px 20px; border: none; border-radius: 0; cursor: pointer; transition: all .15s;
  font-size: 14px; font-weight: 700; letter-spacing: .5px;
  background: ${p => p.disabled ? '#333' : '#22c55e'}; color: #fff;
  &:hover:not(:disabled) { background: #16a34a; }
  &:active:not(:disabled) { transform: scale(.98); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const MappingStepName = styled.div`
  padding: 10px 16px; border-bottom: 1px solid var(--card-border);
  display: flex; flex-direction: column; gap: 6px;
`;

const MappingLog = styled.div`
  flex: 1; min-height: 100px; overflow-y: auto; padding: 8px 12px;
  font-size: 10px; font-family: 'JetBrains Mono', 'Fira Code', monospace;
  background: #0a0a0a; color: #a3a3a3; line-height: 1.6;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
`;

/* ─── AI Auto-Map Chat Panel ─── */
const AiChatPanel = styled.div`
  border-top: 1px solid var(--card-border);
  display: flex; flex-direction: column;
  max-height: ${p => p.$expanded ? '50%' : '180px'};
  min-height: 140px;
  transition: max-height .2s;
`;
const AiChatHeader = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 6px 12px;
  font-size: 11px; font-weight: 700; color: var(--text-primary);
  border-bottom: 1px solid var(--card-border);
  background: linear-gradient(135deg, #7c3aed11, #06b6d411);
  cursor: pointer; user-select: none;
  .status { margin-left: auto; font-size: 9px; font-weight: 500; }
  .status.active { color: #22c55e; }
  .status.idle { color: var(--text-muted); }
`;
const AiChatMessages = styled.div`
  flex: 1; overflow-y: auto; padding: 6px 8px;
  font-size: 10px; display: flex; flex-direction: column; gap: 4px;
  background: #0d0d0d;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
`;
const AiChatMsg = styled.div`
  padding: 4px 8px; border-radius: 6px; line-height: 1.5;
  ${p => p.$type === 'step_created' && 'background: #22c55e11; color: #22c55e; border-left: 2px solid #22c55e;'}
  ${p => p.$type === 'capturing' && 'background: #3b82f611; color: #60a5fa;'}
  ${p => p.$type === 'skipped' && 'background: #f59e0b08; color: #78716c; font-style: italic;'}
  ${p => p.$type === 'error' && 'background: #ef444411; color: #ef4444; border-left: 2px solid #ef4444;'}
  ${p => p.$type === 'capture_error' && 'background: #ef444411; color: #ef4444;'}
  ${p => p.$type === 'ai_error' && 'background: #f59e0b11; color: #f59e0b;'}
  ${p => p.$type === 'started' && 'background: #7c3aed11; color: #a78bfa; font-weight: 600;'}
  ${p => p.$type === 'stopped' && 'background: #78716c11; color: #a8a29e; font-weight: 600;'}
  .screenshot-thumb { width: 40px; height: 70px; border-radius: 4px; margin-top: 4px; object-fit: cover; border: 1px solid #333; cursor: pointer; }
  .selectors { font-size: 9px; color: var(--text-muted); margin-top: 2px; }
  .screen-name { font-weight: 700; font-size: 11px; }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

export default function ScriptFlows({ selectedDevice }) {
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [compactView, setCompactView] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testProgress, setTestProgress] = useState(null);
  const [devices, setDevices] = useState([]);
  const [captureDeviceId, setCaptureDeviceId] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [activeAppTab, setActiveAppTab] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  // Interactive test mode
  const [testMode, setTestMode] = useState(false);
  const [testStepIdx, setTestStepIdx] = useState(0);
  const [testResults, setTestResults] = useState([]); // per-step results
  const [scriptRunning, setScriptRunning] = useState(false);
  const [scriptPid, setScriptPid] = useState(null); // PID of launched script for stop
  const [capturePaused, setCapturePaused] = useState(false); // pause auto-capture
  const capturePausedRef = useRef(false); // ref for interval access
  const autoCapRef = useRef(null); // auto-capture interval ref
  const [sessionDoc, setSessionDoc] = useState(null); // generated markdown doc info
  const [testVerifying, setTestVerifying] = useState(false);
  const [, setMirrorFrame] = useState(null);
  const [mirrorStreaming, setMirrorStreaming] = useState(false);
  const mirrorIntervalRef = useRef(null);
  const [gmailLogs, setGmailLogs] = useState([]); // live logs from open_gmail_fixed.py
  const gmailLogRef = useRef(null); // polling interval for gmail logs

  // ─── Mapping mode ───
  const [mappingMode, setMappingMode] = useState(false);
  const [mappingCapturing, setMappingCapturing] = useState(false);
  const [mappingStepName, setMappingStepName] = useState('');
  const [mappingScreenName, setMappingScreenName] = useState('');
  const [mappingActionType, setMappingActionType] = useState('navigation');
  const [mappingLogs, setMappingLogs] = useState([]);
  const [mappingLastCapture, setMappingLastCapture] = useState(null); // { step, ui_elements, discovered_selectors }
  const [mappingDescription, setMappingDescription] = useState('');
  const [mappingNotes, setMappingNotes] = useState('');

  // Compare versions
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareFlowA, setCompareFlowA] = useState('');
  const [compareFlowB, setCompareFlowB] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const mappingMirrorIntervalRef = useRef(null);
  const [mappingMirrorFrame, setMappingMirrorFrame] = useState(null);
  const mappingLogRef = useRef(null);

  // Auto-Map AI state
  const [autoMapping, setAutoMapping] = useState(false);
  const [autoMapLogs, setAutoMapLogs] = useState([]);
  const [autoMapExpanded, setAutoMapExpanded] = useState(false);
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const autoMapSourceRef = useRef(null);
  const autoMapChatRef = useRef(null);

  const addAutoMapLog = useCallback((evt) => {
    setAutoMapLogs(prev => [...prev, { ...evt, time: new Date().toLocaleTimeString() }]);
    setTimeout(() => { if (autoMapChatRef.current) autoMapChatRef.current.scrollTop = autoMapChatRef.current.scrollHeight; }, 50);
  }, []);

  // ─── Fetch single flow with steps (moved before startAutoMap) ───
  const fetchFlow = useCallback(async (id) => {
    try {
      const res = await axios.get(`/api/script-flows/${id}`);
      setSelectedFlow(res.data);
    } catch (err) {
      console.error('Error fetching flow:', err);
    }
  }, []);

  const startAutoMap = useCallback(() => {
    if (!captureDeviceId || !selectedFlowId) return;
    setAutoMapLogs([]);
    setAutoMapping(true);
    setAutoMapExpanded(true);

    const url = `/api/script-flows/${selectedFlowId}/auto-map/start?device_id=${encodeURIComponent(captureDeviceId)}&interval_ms=8000`;
    const es = new EventSource(url);
    autoMapSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        addAutoMapLog(data);
        // If a step was created, refresh the flow
        if (data.type === 'step_created') {
          fetchFlow(selectedFlowId);
        }
      } catch (_) {}
    };
    es.onerror = () => {
      setAutoMapping(false);
      addAutoMapLog({ type: 'error', message: '🔌 Connexion SSE perdue', time: new Date().toLocaleTimeString() });
      es.close();
      autoMapSourceRef.current = null;
    };
  }, [captureDeviceId, selectedFlowId, addAutoMapLog, fetchFlow]);

  const stopAutoMap = useCallback(async () => {
    if (autoMapSourceRef.current) {
      autoMapSourceRef.current.close();
      autoMapSourceRef.current = null;
    }
    setAutoMapping(false);
    try {
      await axios.post(`/api/script-flows/${selectedFlowId}/auto-map/stop`);
    } catch (_) {}
    addAutoMapLog({ type: 'stopped', message: '🛑 Auto-mapping arrêté par l\'utilisateur', time: new Date().toLocaleTimeString() });
  }, [selectedFlowId, addAutoMapLog]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (autoMapSourceRef.current) {
        autoMapSourceRef.current.close();
        autoMapSourceRef.current = null;
      }
    };
  }, []);

  const addMappingLog = useCallback((msg, type = 'info') => {
    setMappingLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), msg, type }]);
    // Auto-scroll
    setTimeout(() => { if (mappingLogRef.current) mappingLogRef.current.scrollTop = mappingLogRef.current.scrollHeight; }, 50);
  }, []);

  // Mapping mirror polling
  useEffect(() => {
    if (mappingMode && captureDeviceId) {
      const poll = () => {
        const img = new window.Image();
        img.onload = () => setMappingMirrorFrame(img.src);
        img.onerror = () => {};
        img.src = `/api/screenshot/${captureDeviceId}?t=${Date.now()}`;
      };
      poll();
      mappingMirrorIntervalRef.current = setInterval(poll, 800);
      return () => clearInterval(mappingMirrorIntervalRef.current);
    } else {
      if (mappingMirrorIntervalRef.current) clearInterval(mappingMirrorIntervalRef.current);
    }
  }, [mappingMode, captureDeviceId]);

  const startMappingMode = () => {
    if (!captureDeviceId || !selectedFlowId) return;
    setMappingMode(true);
    setMappingLogs([]);
    setMappingStepName('');
    setMappingScreenName('');
    setMappingActionType('navigation');
    setCompactView(true);
    addMappingLog(`🗺️ Mode Mapping démarré — Flow: "${selectedFlow?.name}"`, 'info');
    addMappingLog(`📱 Device: ${captureDeviceId}`, 'info');
    addMappingLog(`Naviguez sur l'app, nommez l'écran, puis cliquez "📸 Capturer"`, 'info');
  };

  const stopMappingMode = () => {
    setMappingMode(false);
    setMappingMirrorFrame(null);
    setMappingLastCapture(null);
    setMappingDescription('');
    setMappingNotes('');
    if (mappingMirrorIntervalRef.current) clearInterval(mappingMirrorIntervalRef.current);
    addMappingLog('🛑 Mode Mapping arrêté', 'info');
  };

  const handleMappingCapture = async () => {
    if (!captureDeviceId || !selectedFlowId || mappingCapturing) return;
    setMappingCapturing(true);
    setMappingLastCapture(null);
    const stepName = mappingStepName.trim() || `Écran ${(selectedFlow?.steps?.length || 0) + 1}`;
    addMappingLog(`📸 Capture en cours: "${stepName}"...`, 'info');
    try {
      const res = await axios.post(`/api/script-flows/${selectedFlowId}/capture-new-step`, {
        device_id: captureDeviceId,
        title: stepName,
        screen_name: mappingScreenName.trim() || stepName,
        action_type: mappingActionType,
        description: mappingDescription.trim() || undefined,
        notes: mappingNotes.trim() || undefined,
      });
      if (res.data.success) {
        const step = res.data.step;
        addMappingLog(`✅ Étape #${step.step_order} "${step.title}" créée`, 'success');
        if (res.data.ui_elements) {
          addMappingLog(`   🔎 ${res.data.ui_elements.resource_ids} IDs, ${res.data.ui_elements.content_descs} descs, ${res.data.ui_elements.texts} textes`, 'info');
        }
        if (res.data.discovered_selectors?.length > 0) {
          addMappingLog(`   🎯 ${res.data.discovered_selectors.length} sélecteurs auto-ajoutés`, 'success');
        }
        // Store last capture for review panel
        setMappingLastCapture({
          step,
          ui_elements: res.data.ui_elements,
          discovered_selectors: res.data.discovered_selectors || [],
        });
        // Reset fields for next capture
        setMappingStepName('');
        setMappingScreenName('');
        setMappingDescription('');
        setMappingNotes('');
        // Refresh the flow
        await fetchFlow(selectedFlowId);
        // Select the new step
        setSelectedStepId(step.id);
      }
    } catch (err) {
      addMappingLog(`❌ Erreur: ${err.response?.data?.error || err.message}`, 'error');
    }
    setMappingCapturing(false);
  };

  // ─── Fetch flows list ───
  const fetchFlows = useCallback(async () => {
    try {
      const res = await axios.get('/api/script-flows');
      setFlows(res.data);
    } catch (err) {
      console.error('Error fetching flows:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFlows(); }, [fetchFlows]);

  // ─── Fetch devices list ───
  const [testLogs, setTestLogs] = useState([]);
  useEffect(() => {
    axios.get('/api/devices').then(res => {
      const devs = res.data?.devices || res.data || [];
      setDevices(devs);
      // Auto-select Screen Mirror device if available, else first device
      if (selectedDevice?.device_id) {
        setCaptureDeviceId(selectedDevice.device_id);
      } else if (devs.length > 0 && !captureDeviceId) {
        setCaptureDeviceId(devs[0].device_id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync captureDeviceId when Screen Mirror device changes
  useEffect(() => {
    if (selectedDevice?.device_id) {
      setCaptureDeviceId(selectedDevice.device_id);
    }
  }, [selectedDevice?.device_id]);

  useEffect(() => { if (selectedFlowId) fetchFlow(selectedFlowId); }, [selectedFlowId, fetchFlow]);

  const selectedStep = selectedFlow?.steps?.find(s => s.id === selectedStepId);
  const scriptType = SCRIPT_TYPES.find(t => t.value === selectedFlow?.script_type);

  // ─── CRUD handlers ───
  const handleCreateFlow = async (data) => {
    try {
      const res = await axios.post('/api/script-flows', data);
      await fetchFlows();
      setSelectedFlowId(res.data.id);
      setShowCreateModal(false);
    } catch (err) { console.error(err); }
  };

  const handleUpdateFlow = async (data) => {
    try {
      await axios.put(`/api/script-flows/${editingFlow.id}`, data);
      await fetchFlows();
      if (selectedFlowId === editingFlow.id) await fetchFlow(editingFlow.id);
      setEditingFlow(null);
    } catch (err) { console.error(err); }
  };

  const handleDeleteFlow = async (id) => {
    if (!window.confirm('Supprimer ce flux et toutes ses étapes ?')) return;
    try {
      await axios.delete(`/api/script-flows/${id}`);
      if (selectedFlowId === id) { setSelectedFlowId(null); setSelectedFlow(null); }
      await fetchFlows();
    } catch (err) { console.error(err); }
  };

  const handleAddStep = async (parentId = null, branchLabel = null) => {
    if (!selectedFlowId) { alert('Aucun flux sélectionné'); return; }
    try {
      const res = await axios.post(`/api/script-flows/${selectedFlowId}/steps`, {
        title: parentId ? `Cas: ${branchLabel || 'Branche'}` : 'Nouvelle étape',
        action_type: 'action',
        parent_step_id: parentId || null,
        branch_label: branchLabel || null,
      });
      await fetchFlow(selectedFlowId);
      setSelectedStepId(res.data.id);
    } catch (err) {
      console.error('handleAddStep error:', err);
      alert('❌ Erreur ajout étape: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateStep = async (stepId, data) => {
    try {
      await axios.put(`/api/script-flows/steps/${stepId}`, data);
      await fetchFlow(selectedFlowId);
    } catch (err) { console.error(err); }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Supprimer cette étape ?')) return;
    try {
      await axios.delete(`/api/script-flows/steps/${stepId}`);
      if (selectedStepId === stepId) setSelectedStepId(null);
      await fetchFlow(selectedFlowId);
    } catch (err) { console.error(err); }
  };

  const handleAddSelector = async (stepId) => {
    try {
      await axios.post(`/api/script-flows/steps/${stepId}/selectors`, {
        selector_type: 'resource_id',
        selector_value: '',
        is_primary: false,
      });
      await fetchFlow(selectedFlowId);
    } catch (err) { console.error(err); }
  };

  const handleUpdateSelector = async (selId, data) => {
    try {
      await axios.put(`/api/script-flows/selectors/${selId}`, data);
      await fetchFlow(selectedFlowId);
    } catch (err) { console.error(err); }
  };

  const handleDeleteSelector = async (selId) => {
    try {
      await axios.delete(`/api/script-flows/selectors/${selId}`);
      await fetchFlow(selectedFlowId);
    } catch (err) { console.error(err); }
  };

  const handleUploadScreenshot = async (stepId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          await axios.post(`/api/script-flows/${selectedFlowId}/steps/${stepId}/screenshot`, { image: base64 });
          await fetchFlow(selectedFlowId);
        } catch (err) { console.error(err); }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // ─── Capture from Device ───
  const handleCaptureFromDevice = async (stepId) => {
    if (!captureDeviceId || !selectedFlowId || capturing) return;
    setCapturing(true);
    try {
      const res = await axios.post(`/api/script-flows/${selectedFlowId}/steps/${stepId}/capture`, { device_id: captureDeviceId });
      await fetchFlow(selectedFlowId);
      return res.data;
    } catch (err) {
      console.error('Capture error:', err);
      return null;
    } finally {
      setCapturing(false);
    }
  };

  // ─── Test Runner (full audit) ───
  const handleRunTest = async () => {
    if (!selectedFlowId || !selectedFlow?.steps?.length || testRunning) return;
    setTestRunning(true);
    setTestProgress({ current: 0, total: selectedFlow.steps.length, status: 'running', log: [], summary: null });
    try {
      const res = await axios.post(`/api/script-flows/${selectedFlowId}/test`, { device_id: captureDeviceId || undefined });
      setTestProgress(prev => ({ ...prev, status: 'done', log: res.data.log || [], summary: res.data.summary || null }));
      await fetchFlow(selectedFlowId);
    } catch (err) {
      setTestProgress(prev => ({ ...prev, status: 'error', error: err.response?.data?.error || err.message }));
    }
    setTestRunning(false);
  };

  const handleStopTest = () => {
    setTestRunning(false);
    setTestProgress(prev => prev ? { ...prev, status: 'stopped' } : null);
  };

  // ─── Interactive Test Mode ───
  // allStepsOrdered: ALL steps (top-level + branch children) in step_order
  const allStepsOrdered = (selectedFlow?.steps || []).slice().sort((a, b) => a.step_order - b.step_order);
  const topLevelSteps = selectedFlow?.steps?.filter(s => !s.parent_step_id) || []; // eslint-disable-line no-unused-vars
  const [testSessionId, setTestSessionId] = useState(null);

  const addLog = (msg, type = 'info') => {
    setTestLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), msg, type }]);
  };

  const startTestMode = async () => {
    if (!captureDeviceId || !selectedFlow?.steps?.length) return;
    setTestMode(true);
    setTestStepIdx(0);
    setTestResults([]);
    setTestLogs([]);
    setMirrorStreaming(true);
    setCompactView(true);
    addLog(`🧪 Test démarré — Flow: "${selectedFlow.name}" (${allStepsOrdered.length} étapes)`, 'info');
    addLog(`📱 Device: ${captureDeviceId}`, 'info');
    // Create a test session on the backend
    try {
      const res = await axios.post(`/api/script-flows/${selectedFlowId}/test-session`, { device_id: captureDeviceId });
      const sid = res.data.session?.id || null;
      setTestSessionId(sid);
      addLog(`✅ Session #${sid} créée (${res.data.session?.device_model || ''} Android ${res.data.session?.android_version || ''})`, 'success');
    } catch (err) {
      console.error('Failed to create test session:', err);
      setTestSessionId(null);
      addLog(`❌ Échec création session: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  const stopTestMode = async () => {
    // Stop auto-capture
    if (autoCapRef.current) { clearInterval(autoCapRef.current); autoCapRef.current = null; }
    // Stop Gmail log polling
    if (gmailLogRef.current) { clearInterval(gmailLogRef.current); gmailLogRef.current = null; }
    // Kill script process if running
    if (scriptPid) {
      try { await axios.post('/api/stop-process', { pid: scriptPid }); } catch (_) {}
      setScriptPid(null);
    }
    // Finish session if exists
    if (testSessionId && selectedFlowId) {
      try {
        await axios.post(`/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/finish`);
        addLog('📋 Session terminée', 'info');
      } catch (e) { console.error('Failed to finish session:', e); }
    }
    setTestMode(false);
    setMirrorStreaming(false);
    setTestSessionId(null);
    setScriptRunning(false);
    setCapturePaused(false);
    setGmailLogs([]);
    if (mirrorIntervalRef.current) clearInterval(mirrorIntervalRef.current);
  };

  // Helper: capture a single step (screenshot + UI dump + selector matching)
  const captureStep = async (stepIdx, step, label = '') => {
    const totalSteps = allStepsOrdered.length;
    setTestStepIdx(stepIdx);
    setSelectedStepId(step.id);
    addLog(`🔍 ${label}Capture étape ${stepIdx + 1}/${totalSteps}: "${step.title}"`, 'info');
    try {
      let result;
      if (testSessionId) {
        const r = await axios.post(
          `/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/step/${step.id}`,
          { device_id: captureDeviceId }
        );
        result = { stepId: step.id, stepIdx, title: step.title, ...r.data };
      } else {
        const r = await axios.post(`/api/script-flows/${selectedFlowId}/steps/${step.id}/capture`, { device_id: captureDeviceId });
        result = { stepId: step.id, stepIdx, title: step.title, ...r.data };
      }
      setTestResults(prev => { const n = [...prev]; n[stepIdx] = result; return n; });
      if (result.screenshot) addLog(`  📸 ${result.screenshot.split('/').pop()}`, 'success');
      if (result.ui_elements) addLog(`  🔎 ${result.ui_elements.resource_ids || 0} IDs, ${result.ui_elements.content_descs || 0} descs`, 'info');
      const sel = result.selectors;
      if (sel) addLog(`  ${sel.missing === 0 ? '✅' : '⚠️'} Sélecteurs: ${sel.matched}/${sel.total}`, sel.missing > 0 ? 'warn' : 'success');
      if (result.duration_ms) addLog(`  ⏱️ ${result.duration_ms}ms`, 'info');
      return result;
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      addLog(`  ❌ Erreur capture: ${errMsg}`, 'error');
      const errResult = { stepId: step.id, stepIdx, title: step.title, error: errMsg };
      setTestResults(prev => { const n = [...prev]; n[stepIdx] = errResult; return n; });
      return errResult;
    }
  };

  // Generate session document
  const generateSessionDoc = async () => {
    if (!testSessionId || !selectedFlowId) return null;
    try {
      const r = await axios.post(`/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/generate-doc`);
      if (r.data.success) {
        setSessionDoc(r.data);
        addLog(`📄 Document généré: ${r.data.filename}`, 'success');
        return r.data;
      }
    } catch (err) {
      addLog(`❌ Erreur génération doc: ${err.response?.data?.error || err.message}`, 'error');
    }
    return null;
  };

  // Helper: get a lightweight UI fingerprint (resource IDs + texts) to detect screen changes
  const getScreenFingerprint = async () => {
    try {
      const r = await axios.post('/api/devices/debug/ui-dump', { device_id: captureDeviceId });
      if (r.data.success && r.data.elements) {
        const rids = r.data.elements.map(e => e.resource_id).filter(Boolean).sort().join('|');
        const texts = r.data.elements.map(e => e.text).filter(Boolean).sort().join('|');
        return `${rids}::${texts}`;
      }
    } catch (_) {}
    return null;
  };

  // Keep capturePausedRef in sync
  useEffect(() => { capturePausedRef.current = capturePaused; }, [capturePaused]);

  // ─── Gmail live log polling ───
  const startGmailLogPolling = useCallback(() => {
    if (gmailLogRef.current) clearInterval(gmailLogRef.current);
    setGmailLogs([]);
    gmailLogRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`/api/gmail-scripts/logs/${captureDeviceId}`);
        if (r.data.success && r.data.actions) {
          setGmailLogs(r.data.actions);
        }
      } catch (_) {}
    }, 3000);
  }, [captureDeviceId]);

  const stopGmailLogPolling = useCallback(() => {
    if (gmailLogRef.current) { clearInterval(gmailLogRef.current); gmailLogRef.current = null; }
  }, []);

  // Clean up gmail log polling on unmount
  useEffect(() => () => stopGmailLogPolling(), [stopGmailLogPolling]);

  // ─── Helper: launch the Gmail script (shared by both modes) ───
  const launchGmailScript = async (background = false) => {
    addLog(`🚀 Lancement du script Gmail creation (${background ? 'arrière-plan' : 'terminal'})...`, 'info');
    try {
      const scriptRes = await axios.post('/api/gmail-scripts/create-gmail', {
        device_id: captureDeviceId,
        openTerminal: !background,
      });
      if (scriptRes.data.success) {
        const pid = scriptRes.data.pid;
        setScriptPid(pid);
        addLog(`✅ Script lancé (PID: ${pid}) — ${scriptRes.data.message}`, 'success');
        startGmailLogPolling();
        return pid;
      } else {
        addLog(`❌ Échec lancement: ${scriptRes.data.error}`, 'error');
        return null;
      }
    } catch (err) {
      addLog(`❌ Erreur lancement: ${err.response?.data?.error || err.message}`, 'error');
      return null;
    }
  };

  // ─── Launch script WITHOUT auto-capture ───
  const launchScriptOnly = async () => {
    if (!captureDeviceId || !selectedFlowId || scriptRunning) return;
    setScriptRunning(true);
    setSessionDoc(null);
    setCapturePaused(false);
    const pid = await launchGmailScript(false);
    if (!pid) { setScriptRunning(false); return; }
    addLog('ℹ️ Mode sans auto-capture — utilisez le bouton "Capturer" manuellement', 'info');
  };

  // ─── Launch script WITH auto-capture ───
  const launchScriptWithCapture = async () => {
    if (!captureDeviceId || !selectedFlowId || scriptRunning) return;
    setScriptRunning(true);
    setSessionDoc(null);
    setCapturePaused(false);
    const totalSteps = allStepsOrdered.length;

    // 1. Capture BEFORE script starts
    let lastFingerprint = null;
    if (allStepsOrdered.length > 0) {
      addLog('📸 Capture initiale AVANT lancement du script...', 'info');
      await captureStep(0, allStepsOrdered[0], '[Pré-action] ');
      lastFingerprint = await getScreenFingerprint();
      await fetchFlow(selectedFlowId);
    }

    // 2. Launch the Gmail script
    const pid = await launchGmailScript(false);
    if (!pid) { setScriptRunning(false); return; }

    // 3. Screen-change-driven auto-capture
    addLog('🔄 Auto-capture activé (détection changement d\'écran)', 'info');
    let stepIdx = 1;
    let polling = false;

    autoCapRef.current = setInterval(async () => {
      if (polling) return;
      if (capturePausedRef.current) return; // skip when paused
      if (stepIdx >= totalSteps) {
        clearInterval(autoCapRef.current);
        autoCapRef.current = null;
        addLog('✅ Auto-capture terminé — toutes les étapes capturées', 'success');
        setScriptRunning(false);
        stopGmailLogPolling();
        if (testSessionId && selectedFlowId) {
          try {
            await axios.post(`/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/finish`);
            addLog('📋 Session finalisée automatiquement', 'success');
          } catch (e) { /* ignore */ }
        }
        await generateSessionDoc();
        await fetchFlow(selectedFlowId);
        return;
      }

      polling = true;
      try {
        const currentFingerprint = await getScreenFingerprint();
        if (!currentFingerprint) { polling = false; return; }
        if (lastFingerprint && currentFingerprint !== lastFingerprint) {
          const step = allStepsOrdered[stepIdx];
          if (!step) { stepIdx++; polling = false; return; }
          addLog(`🔔 Changement d'écran détecté — attente 3s avant capture étape ${stepIdx + 1}...`, 'info');
          await new Promise(resolve => setTimeout(resolve, 3000));
          await captureStep(stepIdx, step, '[Auto] ');
          lastFingerprint = await getScreenFingerprint();
          await fetchFlow(selectedFlowId);
          stepIdx++;
        }
      } catch (err) {
        addLog(`⚠️ Erreur polling: ${err.message}`, 'warn');
      }
      polling = false;
    }, 4000);
  };

  // ─── Toggle pause/resume auto-capture ───
  const toggleCapturePause = () => {
    setCapturePaused(prev => {
      const next = !prev;
      addLog(next ? '⏸️ Auto-capture en pause' : '▶️ Auto-capture repris', 'info');
      return next;
    });
  };

  // ─── Stop script (kill process + stop capture) ───
  const stopScript = async () => {
    // Stop auto-capture
    if (autoCapRef.current) { clearInterval(autoCapRef.current); autoCapRef.current = null; }
    stopGmailLogPolling();
    // Kill the Python process
    if (scriptPid) {
      addLog(`🛑 Arrêt du script (PID: ${scriptPid})...`, 'info');
      try {
        await axios.post('/api/stop-process', { pid: scriptPid });
        addLog(`✅ Signal d'arrêt envoyé au PID ${scriptPid}`, 'success');
      } catch (err) {
        addLog(`⚠️ Erreur arrêt: ${err.response?.data?.error || err.message}`, 'warn');
      }
      setScriptPid(null);
    }
    setScriptRunning(false);
    setCapturePaused(false);
    // Finalize session and generate doc
    if (testSessionId && selectedFlowId) {
      try {
        await axios.post(`/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/finish`);
        addLog('📋 Session finalisée', 'info');
      } catch (e) { /* ignore */ }
    }
    await generateSessionDoc();
    addLog('🛑 Script arrêté', 'info');
  };

  // ─── Manual capture trigger (for no-autocapture mode) ───
  const manualCapture = async () => {
    const step = allStepsOrdered[testStepIdx];
    if (!step || capturing) return;
    await captureStep(testStepIdx, step, '[Manuel] ');
    await fetchFlow(selectedFlowId);
  };

  // Mirror polling
  useEffect(() => {
    if (mirrorStreaming && captureDeviceId) {
      const poll = () => {
        const img = new window.Image();
        img.onload = () => setMirrorFrame(img.src);
        img.onerror = () => {};
        img.src = `/api/screenshot/${captureDeviceId}?t=${Date.now()}`;
      };
      poll();
      mirrorIntervalRef.current = setInterval(poll, 600);
      return () => clearInterval(mirrorIntervalRef.current);
    } else {
      if (mirrorIntervalRef.current) clearInterval(mirrorIntervalRef.current);
    }
  }, [mirrorStreaming, captureDeviceId]);

  const verifyCurrentStep = async () => {
    if (!selectedFlowId || testVerifying) return;
    const step = allStepsOrdered[testStepIdx];
    if (!step) return;
    setTestVerifying(true);
    addLog(`🔍 Vérification étape ${testStepIdx + 1}/${allStepsOrdered.length}: "${step.title}"${step.branch_label ? ` [${step.branch_label}]` : ''}`, 'info');
    try {
      let result;
      if (testSessionId) {
        const res = await axios.post(
          `/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/step/${step.id}`,
          { device_id: captureDeviceId }
        );
        result = { stepId: step.id, stepIdx: testStepIdx, title: step.title, ...res.data };
      } else {
        const res = await axios.post(`/api/script-flows/${selectedFlowId}/steps/${step.id}/capture`, { device_id: captureDeviceId });
        result = { stepId: step.id, stepIdx: testStepIdx, title: step.title, ...res.data };
      }
      setTestResults(prev => {
        const next = [...prev];
        next[testStepIdx] = result;
        return next;
      });
      // Log results
      if (result.screenshot) addLog(`  📸 Screenshot sauvé: ${result.screenshot.split('/').pop()}`, 'success');
      if (result.ui_elements) addLog(`  🔎 UI Dump: ${result.ui_elements.resource_ids || 0} IDs, ${result.ui_elements.content_descs || 0} descs, ${result.ui_elements.texts || 0} textes`, 'info');
      const sel = result.selectors;
      if (sel) {
        const emoji = sel.missing === 0 ? '✅' : '⚠️';
        addLog(`  ${emoji} Sélecteurs: ${sel.matched}/${sel.total} trouvés${sel.missing > 0 ? ` (${sel.missing} manquants)` : ''}`, sel.missing > 0 ? 'warn' : 'success');
      }
      if (result.new_resource_ids?.length > 0) addLog(`  🆕 ${result.new_resource_ids.length} nouveaux IDs: ${result.new_resource_ids.slice(0, 3).join(', ')}${result.new_resource_ids.length > 3 ? '...' : ''}`, 'info');
      if (result.duration_ms) addLog(`  ⏱️ ${result.duration_ms}ms`, 'info');
      await fetchFlow(selectedFlowId);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      addLog(`  ❌ Erreur: ${errMsg}`, 'error');
      setTestResults(prev => {
        const next = [...prev];
        next[testStepIdx] = { stepId: step.id, stepIdx: testStepIdx, title: step.title, error: errMsg };
        return next;
      });
    }
    setTestVerifying(false);
  };

  const testReportSummary = testResults.length > 0 ? {
    total: allStepsOrdered.length,
    verified: testResults.filter(r => r && !r.error).length,
    errors: testResults.filter(r => r?.error).length,
    matched: testResults.reduce((s, r) => s + (r?.selectors?.matched || 0), 0),
    missing: testResults.reduce((s, r) => s + (r?.selectors?.missing || 0), 0),
    totalSel: testResults.reduce((s, r) => s + (r?.selectors?.total || 0), 0),
    path: testResults.filter(r => r && !r.error).map(r => r.title),
    sessionId: testSessionId,
  } : null;

  // ─── Render ───
  return (
    <Container>
      {/* ═══ SIDEBAR ═══ */}
      <Sidebar>
        <SidebarHeader>
          <Layers size={16} color="var(--primary)" />
          <h3>Script Flows</h3>
          <Btn $primary style={{ marginLeft: 'auto', padding: '5px 10px' }} onClick={() => setShowCreateModal(true)}>
            <Plus size={14} />
          </Btn>
        </SidebarHeader>

        {/* App filter tabs */}
        <AppTabBar>
          {APP_TABS.map(tab => {
            const Icon = tab.icon;
            const count = tab.value === 'all' ? flows.length : flows.filter(f => {
              const st = SCRIPT_TYPES.find(t => t.value === f.script_type);
              return st?.app === tab.value;
            }).length;
            return (
              <AppTab key={tab.value} $active={activeAppTab === tab.value} $color={tab.color}
                onClick={() => { setActiveAppTab(tab.value); setVersionFilter('all'); }}>
                <Icon size={12} /> {tab.label} {count > 0 && <span style={{ opacity: .6 }}>({count})</span>}
              </AppTab>
            );
          })}
        </AppTabBar>

        {/* Version filter */}
        {(() => {
          const filteredByApp = activeAppTab === 'all' ? flows : flows.filter(f => {
            const st = SCRIPT_TYPES.find(t => t.value === f.script_type);
            return st?.app === activeAppTab;
          });
          const versions = [...new Set(filteredByApp.map(f => f.app_version).filter(Boolean))];
          if (versions.length > 1) {
            return (
              <VersionFilter>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>Version:</span>
                <Select style={{ flex: 1, fontSize: 11, padding: '3px 6px' }} value={versionFilter} onChange={e => setVersionFilter(e.target.value)}>
                  <option value="all">Toutes</option>
                  {versions.map(v => <option key={v} value={v}>{v}</option>)}
                </Select>
              </VersionFilter>
            );
          }
          return null;
        })()}

        <SidebarList>
          {(() => {
            let filtered = flows;
            if (activeAppTab !== 'all') {
              filtered = filtered.filter(f => {
                const st = SCRIPT_TYPES.find(t => t.value === f.script_type);
                return st?.app === activeAppTab;
              });
            }
            if (versionFilter !== 'all') {
              filtered = filtered.filter(f => f.app_version === versionFilter);
            }

            if (filtered.length === 0) {
              return (
                <EmptyState style={{ padding: '40px 20px' }}>
                  <Layers size={32} />
                  <p>{flows.length === 0 ? 'Aucun flux créé.' : 'Aucun flux pour ce filtre.'}<br/>Cliquez sur + pour créer.</p>
                </EmptyState>
              );
            }

            return filtered.map(flow => {
              const type = SCRIPT_TYPES.find(t => t.value === flow.script_type) || SCRIPT_TYPES[6];
              const FIcon = type.icon;
              return (
                <FlowItem key={flow.id} $active={selectedFlowId === flow.id} onClick={() => { setSelectedFlowId(flow.id); setSelectedStepId(null); }}>
                  <FlowIcon $color={flow.color || type.color}><FIcon size={16} /></FlowIcon>
                  <FlowMeta>
                    <div className="name">{flow.name}</div>
                    <div className="sub">{flow.step_count || 0} étapes • v{flow.version}{flow.app_version ? ` • ${flow.app_version}` : ''}</div>
                  </FlowMeta>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <Btn style={{ padding: 4, background: 'transparent' }} onClick={(e) => { e.stopPropagation(); setEditingFlow(flow); }} title="Modifier">
                      <Edit3 size={12} />
                    </Btn>
                    <Btn style={{ padding: 4, background: 'transparent', color: '#8b5cf6' }} title="Cloner (nouvelle version)"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Cloner "${flow.name}" en nouvelle version ?`)) return;
                        try {
                          const res = await axios.post(`/api/script-flows/${flow.id}/clone`);
                          if (res.data.success) {
                            await fetchFlows();
                            setSelectedFlowId(res.data.flow.id);
                          }
                        } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
                      }}>
                      <Copy size={12} />
                    </Btn>
                    <Btn style={{ padding: 4, background: 'transparent', color: '#ef4444' }} onClick={(e) => { e.stopPropagation(); handleDeleteFlow(flow.id); }} title="Supprimer">
                      <Trash2 size={12} />
                    </Btn>
                  </div>
                </FlowItem>
              );
            });
          })()}
        </SidebarList>

        {/* ═══ AI AUTO-MAP CHAT ═══ */}
        {autoMapLogs.length > 0 && (
          <AiChatPanel $expanded={autoMapExpanded}>
            <AiChatHeader onClick={() => setAutoMapExpanded(e => !e)}>
              <Bot size={13} color="#7c3aed" />
              AI Auto-Map
              <span className={`status ${autoMapping ? 'active' : 'idle'}`}>
                {autoMapping ? '● Actif' : '○ Inactif'}
              </span>
            </AiChatHeader>
            <AiChatMessages ref={autoMapChatRef}>
              {autoMapLogs.map((log, i) => (
                <AiChatMsg key={i} $type={log.type}>
                  <span style={{ opacity: 0.5, marginRight: 4, fontSize: 9 }}>{log.time}</span>
                  {log.type === 'step_created' ? (
                    <div>
                      <div className="screen-name">{log.screen_name}</div>
                      <div>{log.description}</div>
                      <div className="selectors">
                        {log.selectors_added} sélecteurs • {log.selectors?.resource_ids || 0} IDs • {log.selectors?.content_descs || 0} descs
                      </div>
                      {log.screenshot_path && (
                        <img className="screenshot-thumb" src={log.screenshot_path} alt={log.screen_name}
                          onClick={() => { if (log.step_id) setSelectedStepId(log.step_id); }} />
                      )}
                    </div>
                  ) : (
                    <span>{log.message}</span>
                  )}
                </AiChatMsg>
              ))}
            </AiChatMessages>
          </AiChatPanel>
        )}
      </Sidebar>

      {/* ═══ MAIN AREA ═══ */}
      <MainArea>
        {selectedFlow ? (
          <>
            <MainHeader>
              <FlowIcon $color={scriptType?.color || '#6366f1'} style={{ width: 40, height: 40, borderRadius: 10 }}>
                {scriptType && <scriptType.icon size={20} />}
              </FlowIcon>
              <FlowTitle>
                <h2>
                  {selectedFlow.name}
                  <StepBadge $color={scriptType?.color || '#6366f1'}>{scriptType?.label}</StepBadge>
                </h2>
                <div className="version">
                  v{selectedFlow.version} {selectedFlow.app_version && `• App ${selectedFlow.app_version}`}
                  {selectedFlow.description && ` • ${selectedFlow.description}`}
                  {/* Version Switcher */}
                  {(() => {
                    const siblings = flows.filter(f => f.script_type === selectedFlow.script_type && f.id !== selectedFlow.id);
                    if (siblings.length === 0) return null;
                    return (
                      <VersionSwitcher>
                        <div className="trigger" onClick={() => setVersionDropdownOpen(v => !v)}>
                          <GitBranch size={11} /> {siblings.length + 1} versions <ChevronDown size={10} />
                        </div>
                        {versionDropdownOpen && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setVersionDropdownOpen(false)} />
                            <div className="dropdown">
                              <div className="dropdown-item active">
                                <span className="v-badge">v{selectedFlow.version}</span>
                                <span className="v-name">{selectedFlow.name}</span>
                                <span className="v-steps">{selectedFlow.steps?.length || 0} étapes</span>
                              </div>
                              {siblings.sort((a, b) => b.version - a.version).map(f => (
                                <div key={f.id} className="dropdown-item" onClick={() => {
                                  setSelectedFlowId(f.id);
                                  setSelectedStepId(null);
                                  setVersionDropdownOpen(false);
                                }}>
                                  <span className="v-badge">v{f.version}</span>
                                  <span className="v-name">{f.name}</span>
                                  <span className="v-steps">{f.step_count || 0} étapes</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </VersionSwitcher>
                    );
                  })()}
                </div>
              </FlowTitle>
              <div style={{ display: 'flex', gap: 4, background: 'var(--surface-hover)', borderRadius: 8, padding: 2 }}>
                <ToggleBtn $active={!compactView} onClick={() => setCompactView(false)} title="Vue détaillée">
                  <AlignJustify size={14} />
                </ToggleBtn>
                <ToggleBtn $active={compactView} onClick={() => setCompactView(true)} title="Vue compacte">
                  <List size={14} />
                </ToggleBtn>
              </div>
              {/* Device selector */}
              <Select style={{ fontSize: 10, padding: '4px 8px', maxWidth: 140, borderRadius: 6 }} value={captureDeviceId} onChange={e => setCaptureDeviceId(e.target.value)}>
                {devices.length === 0 && <option value="">Aucun device</option>}
                {devices.map(d => <option key={d.device_id} value={d.device_id}>{d.device_model || d.instagramUsername || d.device_id}</option>)}
              </Select>
              {testMode ? (
                <Btn $danger onClick={stopTestMode}><Square size={14} /> Quitter test</Btn>
              ) : mappingMode ? (
                <Btn $danger onClick={stopMappingMode}><Square size={14} /> Quitter mapping</Btn>
              ) : testRunning ? (
                <Btn $danger onClick={handleStopTest}><Square size={14} /> Stop</Btn>
              ) : (
                <>
                  <Btn style={{ background: '#6366f1', color: '#fff' }} onClick={startMappingMode}
                    disabled={!captureDeviceId}
                    title={!captureDeviceId ? 'Sélectionnez un device d\'abord' : 'Mode Mapping: capturer les écrans un par un'}>
                    <MapPin size={14} /> Mapper
                  </Btn>
                  {autoMapping ? (
                    <Btn style={{ background: '#ef4444', color: '#fff' }} onClick={stopAutoMap}>
                      <StopCircle size={14} /> Stop AI
                    </Btn>
                  ) : (
                    <Btn style={{ background: 'linear-gradient(135deg, #7c3aed, #06b6d4)', color: '#fff', border: 'none' }}
                      onClick={startAutoMap}
                      disabled={!captureDeviceId}
                      title={!captureDeviceId ? 'Sélectionnez un device d\'abord' : 'Auto-Map AI: capture + analyse automatique de chaque écran'}>
                      <Zap size={14} /> Auto-Map
                    </Btn>
                  )}
                  <Btn style={{ background: '#22c55e', color: '#fff' }} onClick={startTestMode}
                    disabled={!selectedFlow?.steps?.length || !captureDeviceId}
                    title={!captureDeviceId ? 'Sélectionnez un device d\'abord' : 'Test interactif avec Screen Mirror'}>
                    <Play size={14} /> Tester
                  </Btn>
                  <Btn style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e33' }}
                    onClick={handleRunTest} disabled={!selectedFlow?.steps?.length}
                    title="Audit complet automatique">
                    <Crosshair size={14} /> Audit
                  </Btn>
                </>
              )}
              <Btn $primary onClick={() => handleAddStep()}><Plus size={14} /> Étape</Btn>
              <Btn onClick={() => fetchFlow(selectedFlowId)} title="Rafraîchir"><RefreshCw size={14} /></Btn>
              {/* ⋯ More menu */}
              <div style={{ position: 'relative' }}>
                <Btn style={{ padding: '6px 8px', fontSize: 14, fontWeight: 800, letterSpacing: 2 }}
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)} title="Plus d'options">
                  ⋯
                </Btn>
                {moreMenuOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMoreMenuOpen(false)} />
                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 6, zIndex: 100, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 2, boxShadow: '0 8px 32px rgba(0,0,0,.4)' }}>
                      <Btn style={{ justifyContent: 'flex-start', width: '100%', background: '#8b5cf611', color: '#8b5cf6', fontSize: 11, padding: '8px 12px', borderRadius: 6 }}
                        onClick={async () => {
                          setMoreMenuOpen(false);
                          try {
                            const res = await axios.post(`/api/script-flows/${selectedFlowId}/export-dossier`);
                            if (res.data.success) alert(`📦 Dossier exporté !\n\n📁 ${res.data.export_path}\n\n📸 ${res.data.files.screenshots} screenshots\n📄 ${res.data.files.xml_dumps} XML dumps`);
                          } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
                        }}>
                        <FolderOpen size={13} /> Exporter dossier IA
                      </Btn>
                      {captureDeviceId && (
                        <Btn style={{ justifyContent: 'flex-start', width: '100%', background: '#06b6d411', color: '#06b6d4', fontSize: 11, padding: '8px 12px', borderRadius: 6 }}
                          onClick={async () => {
                            setMoreMenuOpen(false);
                            const appTab = scriptType?.app || 'instagram';
                            const packages = { instagram: 'com.instagram.android', twitter: 'com.twitter.android', gmail: 'com.google.android.gm', google: 'com.google.android.googlequicksearchbox' };
                            const pkg = packages[appTab];
                            if (!pkg) { alert('Package inconnu pour cette app'); return; }
                            try {
                              const res = await axios.get(`/api/script-flows/detect-app-version?device_id=${captureDeviceId}&package_name=${pkg}`);
                              if (res.data.success) {
                                if (window.confirm(`📱 ${pkg}\n\nVersion: ${res.data.version}\nDevice: ${res.data.device_model}\nAndroid: ${res.data.android_version}\n\nMettre à jour le flow ?`)) {
                                  await axios.put(`/api/script-flows/${selectedFlowId}`, { app_version: `${appTab} ${res.data.version}` });
                                  await fetchFlow(selectedFlowId);
                                }
                              }
                            } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
                          }}>
                          <Smartphone size={13} /> Détecter version app
                        </Btn>
                      )}
                      <Btn style={{ justifyContent: 'flex-start', width: '100%', background: '#0ea5e911', color: '#0ea5e9', fontSize: 11, padding: '8px 12px', borderRadius: 6 }}
                        onClick={() => { setMoreMenuOpen(false); setCompareFlowA(String(selectedFlowId)); setCompareFlowB(''); setCompareResult(null); setShowCompareModal(true); }}>
                        <GitBranch size={13} /> Comparer versions
                      </Btn>
                      {selectedFlow?.script_type === 'gmail' && (
                        <Btn style={{ justifyContent: 'flex-start', width: '100%', background: '#f59e0b11', color: '#f59e0b', fontSize: 11, padding: '8px 12px', borderRadius: 6 }}
                          onClick={async () => {
                            setMoreMenuOpen(false);
                            if (!window.confirm('Remplacer toutes les étapes par la séquence Gmail ?')) return;
                            try {
                              await axios.post(`/api/script-flows/${selectedFlowId}/sync-gmail-steps`);
                              await fetchFlow(selectedFlowId);
                            } catch (e) { alert('❌ ' + (e.response?.data?.error || e.message)); }
                          }}>
                          <RefreshCw size={13} /> Sync Gmail
                        </Btn>
                      )}
                    </div>
                  </>
                )}
              </div>
            </MainHeader>

            <FlowCanvas>
              <StepColumn>
                {/* Test progress banner */}
                {testProgress && (
                  <TestBanner>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {testProgress.status === 'running' && <><SpinnerIcon><Loader size={14} /></SpinnerIcon> Audit en cours...</>}
                        {testProgress.status === 'done' && <><CheckCircle2 size={14} color="#22c55e" /> Audit terminé</>}
                        {testProgress.status === 'error' && <span style={{ color: '#ef4444' }}><AlertTriangle size={14} /> Erreur: {testProgress.error}</span>}
                        {testProgress.status === 'stopped' && <span style={{ color: '#f59e0b' }}><Square size={14} /> Test arrêté</span>}
                      </div>
                      {testProgress.summary && (
                        <div style={{ display: 'flex', gap: 12, fontSize: 11, flexWrap: 'wrap' }}>
                          <span style={{ color: '#22c55e', fontWeight: 700 }}>Match: {testProgress.summary.match_rate}</span>
                          <span>Sélecteurs: {testProgress.summary.matched}/{testProgress.summary.total_selectors}</span>
                          {testProgress.summary.missing > 0 && <span style={{ color: '#ef4444' }}>Manquants: {testProgress.summary.missing}</span>}
                          <span style={{ color: 'var(--text-muted)' }}>{testProgress.summary.duration_s}s • {testProgress.summary.device?.model}</span>
                        </div>
                      )}
                      {testProgress.log?.length > 0 && testProgress.status === 'done' && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', maxHeight: 80, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                          {testProgress.log.filter(l => l.status === 'partial' || l.status === 'error').map((l, i) => (
                            <div key={i} style={{ color: l.status === 'error' ? '#ef4444' : '#f59e0b' }}>
                              {l.status === 'error' ? '❌' : '⚠️'} #{l.step} {l.title}
                              {l.selectors && ` — ${l.selectors.missing} manquants: ${l.selectors.missing_details?.join(', ')}`}
                              {l.error && ` — ${l.error}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Btn style={{ padding: '3px 8px', fontSize: 10, alignSelf: 'flex-start' }} onClick={() => setTestProgress(null)}><X size={12} /></Btn>
                  </TestBanner>
                )}

                {(!selectedFlow.steps || selectedFlow.steps.length === 0) ? (
                  <EmptyState>
                    <GitBranch size={40} />
                    <h3>Aucune étape</h3>
                    <p>Ajoutez des étapes pour construire le flux visuel de ce script.</p>
                    <Btn $primary onClick={() => handleAddStep()}><Plus size={14} /> Ajouter la première étape</Btn>
                  </EmptyState>
                ) : (
                  selectedFlow.steps.filter(s => !s.parent_step_id).map((step, idx) => {
                    const actionType = ACTION_TYPES.find(a => a.value === step.action_type) || ACTION_TYPES[0];
                    const isActive = selectedStepId === step.id;

                    // Parse condition branches (shared by both views)
                    const branches = step.is_conditional && step.condition_label
                      ? step.condition_label.split('|').map(b => b.trim()).filter(Boolean)
                      : [];
                    const branchColors = ['#22c55e', '#f59e0b', '#6366f1', '#ef4444'];
                    const allChildSteps = branches.length >= 2
                      ? (selectedFlow.steps || []).filter(cs => cs.parent_step_id === step.id)
                      : [];

                    /* ── COMPACT VIEW ── */
                    if (compactView) {
                      const stepTestIdx = testMode ? allStepsOrdered.findIndex(s => s.id === step.id) : -1;
                      const stepTestResult = stepTestIdx >= 0 ? testResults[stepTestIdx] : null;
                      return (
                        <div key={step.id}>
                          {idx > 0 && <CompactConnector />}
                          <CompactRow $active={isActive} onClick={() => { setSelectedStepId(step.id); if (testMode && stepTestIdx >= 0) setTestStepIdx(stepTestIdx); }}
                            style={testMode && stepTestIdx === testStepIdx ? { borderColor: '#22c55e', background: '#22c55e0a', boxShadow: '0 0 0 1px #22c55e33' } : testMode && stepTestResult ? { borderColor: stepTestResult.error ? '#ef444444' : '#22c55e44' } : {}}>
                            <StepNumber $color={testMode && stepTestIdx === testStepIdx ? '#22c55e' : actionType.color} style={{ width: 24, height: 24, fontSize: 11, borderRadius: 6 }}>
                              {testMode && stepTestResult ? (stepTestResult.error ? '✗' : '✓') : idx + 1}
                            </StepNumber>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.title}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                                {step.screen_name && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Monitor size={9} /> {step.screen_name}</span>}
                                {step.selectors?.length > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Crosshair size={9} /> {step.selectors.length}</span>}
                                {step.screenshot_path && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Image size={9} /> ✓</span>}
                              </div>
                            </div>
                            <StepBadge $color={actionType.color} style={{ fontSize: 9, padding: '1px 6px' }}>{actionType.label}</StepBadge>
                            {step.is_conditional && <StepBadge $color="#ec4899" style={{ fontSize: 9, padding: '1px 6px' }}>Cond</StepBadge>}
                          </CompactRow>

                          {/* Compact branching: side-by-side boxes */}
                          {branches.length >= 2 && (
                            <>
                              <CompactConnector />
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--card-bg)', padding: '1px 8px', borderRadius: 8, border: '1px solid #ec489933', fontSize: 9, fontWeight: 700, color: '#ec4899' }}>
                                  <GitBranch size={9} /> {branches.length} chemins
                                </div>
                              </div>
                              <BranchContainer style={{ gap: 8, marginTop: 4 }}>
                                {branches.map((branch, bi) => {
                                  const c = branchColors[bi % branchColors.length];
                                  const branchKey = `Cas ${String.fromCharCode(65 + bi)}`;
                                  const childSteps = allChildSteps.filter(cs => cs.branch_label === branchKey || cs.branch_label === branch.split('→')[0]?.trim());
                                  return (
                                    <BranchArm key={bi} style={{ alignItems: 'stretch', background: `${c}08`, borderRadius: 8, padding: 6, border: `1px dashed ${c}30` }}>
                                      <BranchTag $color={c}>{branchKey}</BranchTag>
                                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 4, lineHeight: 1.3, padding: '0 4px' }}>{branch}</div>
                                      {childSteps.map(cs => {
                                        const csType = ACTION_TYPES.find(a => a.value === cs.action_type) || ACTION_TYPES[0];
                                        const csActive = selectedStepId === cs.id;
                                        const csTestIdx = testMode ? allStepsOrdered.findIndex(s => s.id === cs.id) : -1;
                                        const csTestResult = csTestIdx >= 0 ? testResults[csTestIdx] : null;
                                        return (
                                          <CompactRow key={cs.id} $active={csActive} onClick={() => { setSelectedStepId(cs.id); if (testMode && csTestIdx >= 0) setTestStepIdx(csTestIdx); }}
                                            style={{
                                              marginBottom: 3, padding: '5px 8px',
                                              border: `1px solid ${testMode && csTestIdx === testStepIdx ? '#22c55e' : csActive ? c : c + '30'}`,
                                              background: testMode && csTestIdx === testStepIdx ? '#22c55e0a' : undefined,
                                            }}>
                                            {testMode && csTestResult ? (
                                              <span style={{ fontSize: 10, marginRight: 3 }}>{csTestResult.error ? '✗' : '✓'}</span>
                                            ) : (
                                              <StepBadge $color={csType.color} style={{ fontSize: 8, padding: '1px 5px' }}>{csType.label}</StepBadge>
                                            )}
                                            <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cs.title}</div>
                                            {cs.screenshot_path && <Image size={9} style={{ opacity: .5, flexShrink: 0 }} />}
                                          </CompactRow>
                                        );
                                      })}
                                      <Btn style={{ fontSize: 9, padding: '2px 6px', width: '100%', justifyContent: 'center', borderRadius: 4, marginTop: 2, background: `${c}08`, border: `1px dashed ${c}30`, color: c }}
                                        onClick={(e) => { e.stopPropagation(); handleAddStep(step.id, branchKey); }}>
                                        <Plus size={9} /> Ajouter
                                      </Btn>
                                    </BranchArm>
                                  );
                                })}
                              </BranchContainer>
                              <CompactConnector />
                            </>
                          )}
                        </div>
                      );
                    }

                    /* ── DETAILED VIEW ── */

                    return (
                      <div key={step.id}>
                        {step.branch_label && (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
                            <BranchLabel><GitBranch size={10} /> {step.branch_label}</BranchLabel>
                          </div>
                        )}

                        {idx > 0 && !step.branch_label && (
                          <StepConnector>
                            <ConnectorDot />
                          </StepConnector>
                        )}

                        <StepCard $active={isActive} onClick={() => setSelectedStepId(step.id)}>
                          <StepHeader>
                            <StepNumber $color={actionType.color}>{idx + 1}</StepNumber>
                            <StepTitle>
                              <div className="title">{step.title}</div>
                              {step.screen_name && (
                                <div className="screen"><Monitor size={10} /> {step.screen_name}</div>
                              )}
                            </StepTitle>
                            <StepBadge $color={actionType.color}>{actionType.label}</StepBadge>
                            {step.is_conditional && (
                              <StepBadge $color="#ec4899">Condition</StepBadge>
                            )}
                          </StepHeader>

                          <StepBody>
                            <StepScreenshot onClick={(e) => { e.stopPropagation(); handleUploadScreenshot(step.id); }}>
                              {step.screenshot_path ? (
                                <img src={step.screenshot_path} alt={step.title} />
                              ) : (
                                <div className="placeholder">
                                  <Image size={20} />
                                  <span>Screenshot</span>
                                  <span style={{ fontSize: 9 }}>Cliquer pour ajouter</span>
                                </div>
                              )}
                            </StepScreenshot>

                            <StepInfo>
                              {step.description && <StepDesc>{step.description}</StepDesc>}

                              {step.condition_label && branches.length < 2 && (
                                <div style={{ fontSize: 11, color: '#ec4899', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                                  <AlertTriangle size={12} /> {step.condition_label}
                                </div>
                              )}
                              {branches.length >= 2 && (
                                <div style={{ fontSize: 10, color: '#ec4899', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                                  <GitBranch size={11} /> {branches.length} chemins possibles ↓
                                </div>
                              )}

                              {step.selectors && step.selectors.length > 0 && (
                                <SelectorList>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Crosshair size={10} /> Sélecteurs
                                  </div>
                                  {step.selectors.map(sel => (
                                    <SelectorTag key={sel.id} $primary={sel.is_primary}>
                                      <span className="type">{sel.selector_type}</span>
                                      <span className="value">{sel.selector_value || '—'}</span>
                                      {sel.is_primary && <CheckCircle2 size={10} color="var(--primary)" />}
                                    </SelectorTag>
                                  ))}
                                </SelectorList>
                              )}

                              {step.notes && (
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', borderLeft: '2px solid var(--card-border)', paddingLeft: 8 }}>
                                  {step.notes}
                                </div>
                              )}
                            </StepInfo>
                          </StepBody>
                        </StepCard>

                        {/* ── Decision tree branching ── */}
                        {branches.length >= 2 && (() => {
                          return (
                          <div style={{ padding: '8px 0' }}>
                            <StepConnector style={{ padding: '2px 0' }}><ConnectorDot /></StepConnector>
                            <div style={{ position: 'relative', height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 2, background: 'linear-gradient(90deg, #22c55e44, #ec4899, #f59e0b44)' }} />
                              <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--card-bg)', padding: '2px 12px', borderRadius: 12, border: '1px solid #ec489933' }}>
                                <GitBranch size={11} color="#ec4899" />
                                <span style={{ fontSize: 9, fontWeight: 700, color: '#ec4899', textTransform: 'uppercase', letterSpacing: '.5px' }}>Arbre de décision</span>
                              </div>
                            </div>
                            <BranchContainer style={{ gap: 10, marginTop: 0 }}>
                              {branches.map((branch, bi) => {
                                const colors = branchColors;
                                const icons = ['✓', '✗', '◆', '●'];
                                const c = colors[bi % colors.length];
                                const branchKey = `Cas ${String.fromCharCode(65 + bi)}`;
                                const childSteps = allChildSteps.filter(cs => cs.branch_label === branchKey || cs.branch_label === branch.split('→')[0]?.trim());
                                return (
                                  <BranchArm key={bi} style={{ alignItems: 'stretch' }}>
                                    <BranchTag $color={c}>
                                      {icons[bi % icons.length]} {branchKey}
                                    </BranchTag>
                                    {/* Branch description */}
                                    <div style={{
                                      padding: '8px 10px', borderRadius: 8, fontSize: 11, lineHeight: 1.4,
                                      background: `${c}08`, border: `1px dashed ${c}30`,
                                      color: 'var(--text-muted)', width: '100%', textAlign: 'center', marginBottom: 6,
                                    }}>
                                      {branch}
                                    </div>
                                    {/* Child step cards for this branch */}
                                    {childSteps.map(cs => {
                                      const csType = ACTION_TYPES.find(a => a.value === cs.action_type) || ACTION_TYPES[0];
                                      const csActive = selectedStepId === cs.id;
                                      return (
                                        <div key={cs.id} style={{
                                          background: csActive ? `${c}12` : 'var(--card-bg)',
                                          border: `1.5px solid ${csActive ? c : c + '30'}`,
                                          borderRadius: 10, padding: 10, width: '100%', cursor: 'pointer',
                                          marginBottom: 6, transition: 'all .15s',
                                        }} onClick={(e) => { e.stopPropagation(); setSelectedStepId(cs.id); }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                            <StepBadge $color={csType.color} style={{ fontSize: 8, padding: '1px 5px' }}>{csType.label}</StepBadge>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cs.title}</span>
                                          </div>
                                          {cs.screenshot_path && (
                                            <img src={cs.screenshot_path} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, marginBottom: 6 }} />
                                          )}
                                          {cs.description && <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.4, marginBottom: 4 }}>{cs.description}</div>}
                                          {cs.selectors?.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                              {cs.selectors.map(sel => (
                                                <span key={sel.id} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--card-border)', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                                                  {sel.selector_type}: {sel.selector_value?.substring(0, 20)}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {/* Add child step button */}
                                    <Btn style={{ fontSize: 10, padding: '3px 8px', width: '100%', justifyContent: 'center', borderRadius: 6, marginTop: 2, background: `${c}08`, border: `1px dashed ${c}30`, color: c }}
                                      onClick={(e) => { e.stopPropagation(); handleAddStep(step.id, branchKey); }}>
                                      <Plus size={10} /> Ajouter étape
                                    </Btn>
                                  </BranchArm>
                                );
                              })}
                            </BranchContainer>
                            {/* Converging lines */}
                            <div style={{ position: 'relative', height: 20, marginTop: 4 }}>
                              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 2, background: 'var(--card-border)' }} />
                              <div style={{ position: 'absolute', left: '50%', top: 0, width: 2, height: '100%', background: 'var(--card-border)', transform: 'translateX(-50%)' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', padding: '2px 8px', borderRadius: 4, background: 'var(--surface-hover)' }}>
                                ↓ Continue
                              </div>
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                    );
                  })
                )}

                {selectedFlow.steps && selectedFlow.steps.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                    <Btn style={{ borderRadius: 20, padding: '8px 20px' }} onClick={() => handleAddStep()}>
                      <Plus size={14} /> Ajouter une étape
                    </Btn>
                  </div>
                )}
              </StepColumn>

              {/* ═══ MAPPING MODE PANEL ═══ */}
              {mappingMode && (
                <MappingPanel>
                  <MappingHeader>
                    <h3><MapPin size={14} color="#6366f1" /> Mode Mapping</h3>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#6366f118', color: '#6366f1', border: '1px solid #6366f133' }}>
                      {(selectedFlow?.steps?.length || 0)} étapes
                    </span>
                  </MappingHeader>

                  {/* Screen Mirror */}
                  <MirrorContainer>
                    {mappingMirrorFrame ? (
                      <img src={mappingMirrorFrame} alt="Screen Mirror" />
                    ) : (
                      <div className="no-mirror">
                        <Monitor size={32} />
                        <span>Connexion au device...</span>
                        <span style={{ fontSize: 9 }}>{captureDeviceId}</span>
                      </div>
                    )}
                  </MirrorContainer>

                  {/* Step name input */}
                  <MappingStepName>
                    <Input
                      placeholder={`Nom de l'écran (ex: Home Feed, Profile...)`}
                      value={mappingStepName}
                      onChange={e => setMappingStepName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleMappingCapture(); }}
                      style={{ fontSize: 13, fontWeight: 600 }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Input
                        placeholder="Screen name (optionnel)"
                        value={mappingScreenName}
                        onChange={e => setMappingScreenName(e.target.value)}
                        style={{ flex: 1, fontSize: 11 }}
                      />
                      <Select value={mappingActionType} onChange={e => setMappingActionType(e.target.value)}
                        style={{ width: 110, fontSize: 11, padding: '5px 6px' }}>
                        {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </Select>
                    </div>
                    <TextArea
                      placeholder="Description de l'écran (contexte, ce qu'on peut faire ici...)"
                      value={mappingDescription}
                      onChange={e => setMappingDescription(e.target.value)}
                      style={{ fontSize: 11, minHeight: 36, resize: 'vertical' }}
                      rows={2}
                    />
                    <Input
                      placeholder="Notes (optionnel)"
                      value={mappingNotes}
                      onChange={e => setMappingNotes(e.target.value)}
                      style={{ fontSize: 11 }}
                    />
                  </MappingStepName>

                  {/* Capture button */}
                  <CaptureBtn onClick={handleMappingCapture} disabled={mappingCapturing || !captureDeviceId}>
                    {mappingCapturing ? (
                      <><SpinnerIcon><Loader size={16} /></SpinnerIcon> Capture en cours...</>
                    ) : (
                      <><Camera size={16} /> Capturer cet écran</>
                    )}
                  </CaptureBtn>

                  {/* Last capture review */}
                  {mappingLastCapture && (
                    <div style={{
                      margin: '0 12px', padding: 10, borderRadius: 8,
                      background: '#22c55e08', border: '1px solid #22c55e33',
                      fontSize: 11, display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#22c55e' }}>
                        <CheckCircle2 size={14} />
                        Dernier: #{mappingLastCapture.step.step_order} "{mappingLastCapture.step.title}"
                      </div>
                      {mappingLastCapture.step.screenshot_path && (
                        <img
                          src={mappingLastCapture.step.screenshot_path}
                          alt="Last capture"
                          style={{ width: '100%', borderRadius: 6, border: '1px solid var(--card-border)', maxHeight: 120, objectFit: 'cover' }}
                        />
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {mappingLastCapture.ui_elements && (
                          <>
                            <span style={{ color: '#6366f1' }}>🔑 {mappingLastCapture.ui_elements.resource_ids} IDs</span>
                            <span style={{ color: '#8b5cf6' }}>💬 {mappingLastCapture.ui_elements.content_descs} descs</span>
                            <span style={{ color: '#06b6d4' }}>📝 {mappingLastCapture.ui_elements.texts} textes</span>
                          </>
                        )}
                      </div>
                      {mappingLastCapture.discovered_selectors.length > 0 && (
                        <div style={{ maxHeight: 80, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {mappingLastCapture.discovered_selectors.map((s, i) => (
                            <div key={i} style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)', display: 'flex', gap: 4 }}>
                              <span style={{ color: s.type === 'resource_id' ? '#6366f1' : '#f59e0b', fontWeight: 600, minWidth: 75 }}>{s.type}</span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Btn style={{ fontSize: 10, padding: '3px 8px', alignSelf: 'flex-end', background: 'transparent', color: 'var(--text-muted)' }}
                        onClick={() => { setMappingLastCapture(null); }}
                        title="Masquer ce résumé">
                        <X size={10} /> Fermer
                      </Btn>
                    </div>
                  )}

                  {/* Mapping logs */}
                  <MappingLog ref={mappingLogRef}>
                    {mappingLogs.length === 0 ? (
                      <div style={{ color: '#555', textAlign: 'center', padding: 16 }}>
                        Naviguez sur l'app puis cliquez "Capturer"
                      </div>
                    ) : mappingLogs.map((log, i) => (
                      <div key={i} style={{
                        color: log.type === 'error' ? '#ef4444' : log.type === 'warn' ? '#f59e0b' : log.type === 'success' ? '#22c55e' : '#a3a3a3',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      }}>
                        <span style={{ color: '#555', marginRight: 6 }}>{log.time}</span>{log.msg}
                      </div>
                    ))}
                  </MappingLog>
                </MappingPanel>
              )}

              {/* ═══ TEST MODE: Controls + Logs (Screen Mirror is the existing panel on the right) ═══ */}
              {testMode && (
                <div style={{ width: 340, minWidth: 340, background: 'var(--card-bg)', borderLeft: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: '100%', overflow: 'hidden' }}>
                  {/* Test header */}
                  <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', gap: 8, background: '#22c55e08' }}>
                    <Play size={14} color="#22c55e" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Test Session</span>
                    {testSessionId && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#22c55e18', color: '#22c55e', border: '1px solid #22c55e33' }}>#{testSessionId}</span>}
                  </div>

                  {/* Step controls */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--card-border)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Étape {testStepIdx + 1}/{allStepsOrdered.length}
                      {allStepsOrdered[testStepIdx]?.branch_label && (
                        <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: '#ec489918', color: '#ec4899', border: '1px solid #ec489933' }}>
                          {allStepsOrdered[testStepIdx].branch_label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {allStepsOrdered[testStepIdx]?.title}
                    </div>
                    <TestProgress style={{ marginBottom: 10 }}>
                      <div style={{ width: `${allStepsOrdered.length > 0 ? ((testStepIdx + 1) / allStepsOrdered.length * 100) : 0}%` }} />
                    </TestProgress>

                    {/* Verify result for current step */}
                    {testResults[testStepIdx] && (
                      <div style={{
                        fontSize: 11, padding: '6px 10px', borderRadius: 6, marginBottom: 8,
                        background: testResults[testStepIdx].error ? '#ef444412' : '#22c55e12',
                        border: `1px solid ${testResults[testStepIdx].error ? '#ef444433' : '#22c55e33'}`,
                      }}>
                        {testResults[testStepIdx].error ? (
                          <span style={{ color: '#ef4444' }}>❌ {testResults[testStepIdx].error}</span>
                        ) : (
                          <>
                            <span style={{ color: '#22c55e', fontWeight: 600 }}>
                              ✅ {testResults[testStepIdx].selectors?.matched || 0}/{testResults[testStepIdx].selectors?.total || 0} sélecteurs
                            </span>
                            {testResults[testStepIdx].selectors?.missing > 0 && (
                              <span style={{ color: '#ef4444', marginLeft: 6 }}>• {testResults[testStepIdx].selectors.missing} manquants</span>
                            )}
                            {testResults[testStepIdx].new_resource_ids?.length > 0 && (
                              <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                                🆕 {testResults[testStepIdx].new_resource_ids.length} nouveaux IDs trouvés
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Launch script controls */}
                    {!scriptRunning ? (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <Btn style={{ flex: 1, justifyContent: 'center', background: '#6366f1', color: '#fff', fontSize: 10, fontWeight: 700 }}
                          onClick={launchScriptWithCapture} disabled={!captureDeviceId}>
                          <Play size={11} /> Script + Auto-capture
                        </Btn>
                        <Btn style={{ flex: 1, justifyContent: 'center', background: '#0ea5e9', color: '#fff', fontSize: 10, fontWeight: 700 }}
                          onClick={launchScriptOnly} disabled={!captureDeviceId}>
                          <Play size={11} /> Script seul
                        </Btn>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 10, padding: '6px 10px', borderRadius: 6, marginBottom: 6, background: capturePaused ? '#f59e0b12' : '#6366f112', border: `1px solid ${capturePaused ? '#f59e0b33' : '#6366f133'}`, color: capturePaused ? '#f59e0b' : '#6366f1', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <SpinnerIcon style={capturePaused ? { animation: 'none' } : {}}><Loader size={11} /></SpinnerIcon>
                          {capturePaused ? 'Script en cours — capture en pause' : `Script en cours${autoCapRef.current ? ' — auto-capture actif' : ' — capture manuelle'}`}
                          {scriptPid && <span style={{ marginLeft: 'auto', opacity: .6, fontSize: 9 }}>PID {scriptPid}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                          {autoCapRef.current && (
                            <Btn style={{ flex: 1, justifyContent: 'center', fontSize: 10, background: capturePaused ? '#22c55e18' : '#f59e0b18', color: capturePaused ? '#22c55e' : '#f59e0b', border: `1px solid ${capturePaused ? '#22c55e33' : '#f59e0b33'}` }}
                              onClick={toggleCapturePause}>
                              {capturePaused ? <><Play size={10} /> Reprendre</> : <><Square size={10} /> Pause</>}
                            </Btn>
                          )}
                          <Btn style={{ flex: 1, justifyContent: 'center', fontSize: 10, background: '#22c55e18', color: '#22c55e', border: '1px solid #22c55e33' }}
                            onClick={manualCapture} disabled={capturing}>
                            {capturing ? <SpinnerIcon><Loader size={10} /></SpinnerIcon> : <Crosshair size={10} />} Capturer
                          </Btn>
                          <Btn style={{ flex: 1, justifyContent: 'center', fontSize: 10 }} $danger onClick={stopScript}>
                            <Square size={10} /> Stop
                          </Btn>
                        </div>
                      </>
                    )}

                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn style={{ flex: 1, justifyContent: 'center', fontSize: 11 }}
                        disabled={testStepIdx <= 0}
                        onClick={() => { setTestStepIdx(i => i - 1); setSelectedStepId(allStepsOrdered[testStepIdx - 1]?.id); }}>
                        ← Préc
                      </Btn>
                      <Btn style={{ flex: 2, justifyContent: 'center', background: '#22c55e', color: '#fff', fontSize: 11 }}
                        onClick={verifyCurrentStep} disabled={testVerifying}>
                        {testVerifying ? <><SpinnerIcon><Loader size={12} /></SpinnerIcon> Vérif...</> : <><Crosshair size={12} /> Vérifier</>}
                      </Btn>
                      <Btn style={{ flex: 1, justifyContent: 'center', fontSize: 11 }}
                        disabled={testStepIdx >= allStepsOrdered.length - 1}
                        onClick={() => { setTestStepIdx(i => i + 1); setSelectedStepId(allStepsOrdered[testStepIdx + 1]?.id); }}>
                        Suiv →
                      </Btn>
                    </div>
                  </div>

                  {/* Test report summary */}
                  {testReportSummary && testReportSummary.verified > 0 && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--card-border)', background: 'var(--surface)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle2 size={12} color="#22c55e" /> Rapport de test
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div>Étapes vérifiées: <strong style={{ color: '#22c55e' }}>{testReportSummary.verified}</strong>/{testReportSummary.total}</div>
                        <div>Sélecteurs: <strong style={{ color: '#22c55e' }}>{testReportSummary.matched}</strong>/{testReportSummary.totalSel} trouvés
                          {testReportSummary.missing > 0 && <span style={{ color: '#ef4444' }}> • {testReportSummary.missing} manquants</span>}
                        </div>
                        {testReportSummary.errors > 0 && (
                          <div style={{ color: '#ef4444' }}>Erreurs: {testReportSummary.errors}</div>
                        )}
                        <div style={{ marginTop: 4, fontSize: 9, color: 'var(--text-muted)' }}>
                          Chemin: {testReportSummary.path.join(' → ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                        <Btn style={{ flex: 1, justifyContent: 'center', background: 'var(--primary)', color: '#fff', fontSize: 10 }}
                          onClick={async () => {
                            if (!window.confirm(`Appliquer le chemin testé (${testReportSummary.path.length} étapes) ?`)) return;
                            try {
                              for (let i = 0; i < testResults.length; i++) {
                                const r = testResults[i];
                                if (r && !r.error && r.stepId) {
                                  await axios.put(`/api/script-flows/steps/${r.stepId}`, { step_order: i + 1, status: 'active' });
                                }
                              }
                              addLog('✅ Chemin appliqué', 'success');
                              await fetchFlow(selectedFlowId);
                            } catch (e) { addLog(`❌ ${e.message}`, 'error'); }
                          }}>
                          <Save size={10} /> Appliquer chemin
                        </Btn>
                        <Btn style={{ flex: 1, justifyContent: 'center', background: '#f59e0b', color: '#fff', fontSize: 10 }}
                          onClick={async () => {
                            if (!testSessionId) { addLog('⚠️ Pas de session active', 'warn'); return; }
                            addLog('🔧 Auto-correction en cours...', 'info');
                            try {
                              const r = await axios.post(`/api/script-flows/${selectedFlowId}/test-session/${testSessionId}/auto-correct`);
                              if (r.data.corrections_count > 0) {
                                addLog(`✅ ${r.data.corrections_count} corrections appliquées`, 'success');
                                for (const c of r.data.corrections) {
                                  addLog(`  • ${c.title}: ${c.action}${c.count ? ` (${c.count})` : ''}`, 'info');
                                }
                              } else {
                                addLog('ℹ️ Aucune correction nécessaire', 'info');
                              }
                              await fetchFlow(selectedFlowId);
                            } catch (e) { addLog(`❌ ${e.response?.data?.error || e.message}`, 'error'); }
                          }}>
                          <Settings size={10} /> Auto-corriger
                        </Btn>
                      </div>
                      {/* Generate session document button */}
                      <div style={{ marginTop: 6 }}>
                        <Btn style={{ width: '100%', justifyContent: 'center', background: '#8b5cf6', color: '#fff', fontSize: 10 }}
                          onClick={generateSessionDoc} disabled={!testSessionId}>
                          <FileText size={10} /> Générer document de session
                        </Btn>
                      </div>
                      {sessionDoc && (
                        <div style={{ marginTop: 6, padding: '6px 10px', background: '#8b5cf612', border: '1px solid #8b5cf633', borderRadius: 6, fontSize: 10 }}>
                          <div style={{ fontWeight: 700, color: '#8b5cf6', marginBottom: 2 }}>📄 {sessionDoc.filename}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 9 }}>{sessionDoc.filepath}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generate doc button (visible even without report) */}
                  {testSessionId && !testReportSummary?.verified && (
                    <div style={{ padding: '8px 16px', borderTop: '1px solid var(--card-border)' }}>
                      <Btn style={{ width: '100%', justifyContent: 'center', background: '#8b5cf6', color: '#fff', fontSize: 10 }}
                        onClick={generateSessionDoc} disabled={!testSessionId}>
                        <FileText size={10} /> Générer document de session
                      </Btn>
                      {sessionDoc && (
                        <div style={{ marginTop: 6, padding: '6px 10px', background: '#8b5cf612', border: '1px solid #8b5cf633', borderRadius: 6, fontSize: 10 }}>
                          <div style={{ fontWeight: 700, color: '#8b5cf6', marginBottom: 2 }}>📄 {sessionDoc.filename}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 9 }}>{sessionDoc.filepath}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ═══ GMAIL LIVE LOGS (from open_gmail_fixed.py) ═══ */}
                  {gmailLogs.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--card-border)', maxHeight: 180, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <div style={{ padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--card-border)', background: '#ea433508' }}>
                        <Mail size={11} color="#ea4335" />
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#ea4335', flex: 1 }}>Gmail Script Steps</span>
                        <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{gmailLogs.length} actions</span>
                      </div>
                      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px', fontSize: 10, fontFamily: 'monospace', background: '#0a0a0a', color: '#a3a3a3', lineHeight: 1.5 }}>
                        {gmailLogs.map((action, i) => (
                          <div key={i} style={{
                            color: action.status === 'error' || action.status === 'failed' ? '#ef4444' :
                                   action.status === 'success' || action.status === 'completed' ? '#22c55e' :
                                   action.status === 'running' ? '#6366f1' : '#a3a3a3',
                            padding: '1px 0', display: 'flex', gap: 6, alignItems: 'flex-start',
                          }}>
                            <span style={{ color: '#555', flexShrink: 0 }}>{i + 1}.</span>
                            <span style={{ flex: 1 }}>
                              {action.step || action.action || action.message || JSON.stringify(action)}
                              {action.selector && <span style={{ color: '#8b5cf6', marginLeft: 4 }}>[{action.selector}]</span>}
                              {action.duration && <span style={{ color: '#555', marginLeft: 4 }}>({action.duration})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ═══ DEBUG LOG PANEL ═══ */}
                  <div style={{ flex: 1, minHeight: 120, borderTop: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--card-border)' }}>
                      <Code size={11} color="var(--text-muted)" />
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Logs</span>
                      <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{testLogs.length} entrées</span>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '6px 12px', fontSize: 10, fontFamily: 'monospace', background: '#0a0a0a', color: '#a3a3a3', lineHeight: 1.6 }}>
                      {testLogs.length === 0 ? (
                        <div style={{ color: '#555', textAlign: 'center', padding: 16 }}>Cliquez "Vérifier" pour commencer...</div>
                      ) : testLogs.map((log, i) => (
                        <div key={i} style={{
                          color: log.type === 'error' ? '#ef4444' : log.type === 'warn' ? '#f59e0b' : log.type === 'success' ? '#22c55e' : '#a3a3a3',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        }}>
                          <span style={{ color: '#555', marginRight: 6 }}>{log.time}</span>{log.msg}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ DETAIL PANEL ═══ */}
              {selectedStep && !mappingMode && !testMode && (
                <DetailPanel>
                  <DetailHeader>
                    <h3>Détails de l'étape</h3>
                    <Btn style={{ padding: 4, background: 'transparent' }} onClick={() => setSelectedStepId(null)}><X size={16} /></Btn>
                  </DetailHeader>
                  <StepDetailEditor
                    step={selectedStep}
                    flowType={selectedFlow?.script_type}
                    onUpdate={(data) => handleUpdateStep(selectedStep.id, data)}
                    onDelete={() => handleDeleteStep(selectedStep.id)}
                    onAddSelector={() => handleAddSelector(selectedStep.id)}
                    onUpdateSelector={handleUpdateSelector}
                    onDeleteSelector={handleDeleteSelector}
                    onUploadScreenshot={() => handleUploadScreenshot(selectedStep.id)}
                    onCaptureFromDevice={() => handleCaptureFromDevice(selectedStep.id)}
                    onRefreshFlow={() => fetchFlow(selectedFlowId)}
                    devices={devices}
                    captureDeviceId={captureDeviceId}
                    onDeviceChange={setCaptureDeviceId}
                    capturing={capturing}
                  />
                </DetailPanel>
              )}
            </FlowCanvas>
          </>
        ) : (
          <EmptyState style={{ flex: 1 }}>
            <Layers size={48} style={{ opacity: .3 }} />
            <h3>Script Flow Editor</h3>
            <p>
              Capturez les écrans de vos apps, organisez-les en flux ordonnés,
              ou créez une bibliothèque d'écrans UI réutilisables.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Btn $primary onClick={() => setShowCreateModal(true)}>
                <Plus size={14} /> Créer un flux
              </Btn>
              <Btn style={{ background: '#6366f1', color: '#fff' }}
                onClick={() => { setEditingFlow({ script_type: 'ui-library', name: '', version: '1.0' }); }}>
                <Camera size={14} /> Bibliothèque UI
              </Btn>
            </div>
          </EmptyState>
        )}
      </MainArea>

      {/* ═══ CREATE/EDIT MODAL ═══ */}
      {(showCreateModal || editingFlow) && (
        <FlowModal
          initial={editingFlow}
          devices={devices}
          defaultDeviceId={captureDeviceId}
          onSave={editingFlow ? handleUpdateFlow : handleCreateFlow}
          onClose={() => { setShowCreateModal(false); setEditingFlow(null); }}
        />
      )}

      {/* ═══ COMPARE VERSIONS MODAL ═══ */}
      {showCompareModal && (
        <Overlay onClick={() => setShowCompareModal(false)}>
          <Modal onClick={e => e.stopPropagation()} style={{ minWidth: 500, maxWidth: 700 }}>
            <ModalHeader>
              <GitBranch size={20} color="#0ea5e9" />
              <h3>Comparer deux versions</h3>
              <Btn style={{ padding: 4, background: 'transparent' }} onClick={() => setShowCompareModal(false)}><X size={18} /></Btn>
            </ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', gap: 12 }}>
                <FormGroup style={{ flex: 1 }}>
                  <label>Flow A</label>
                  <select value={compareFlowA} onChange={e => setCompareFlowA(e.target.value)}>
                    <option value="">-- Sélectionner --</option>
                    {flows.map(f => <option key={f.id} value={f.id}>{f.name} v{f.version}</option>)}
                  </select>
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <label>Flow B</label>
                  <select value={compareFlowB} onChange={e => setCompareFlowB(e.target.value)}>
                    <option value="">-- Sélectionner --</option>
                    {flows.map(f => <option key={f.id} value={f.id}>{f.name} v{f.version}</option>)}
                  </select>
                </FormGroup>
              </div>
              <Btn $primary disabled={!compareFlowA || !compareFlowB || comparing}
                onClick={async () => {
                  setComparing(true); setCompareResult(null);
                  try {
                    const res = await axios.post('/api/script-flows/compare-versions', { flow_id_a: parseInt(compareFlowA), flow_id_b: parseInt(compareFlowB) });
                    setCompareResult(res.data);
                  } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
                  finally { setComparing(false); }
                }}>
                {comparing ? <><Loader size={14} /> Comparaison...</> : <><GitBranch size={14} /> Comparer</>}
              </Btn>
              {compareResult && compareResult.success && (
                <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 12, fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>
                    {compareResult.flow_a.name} v{compareResult.flow_a.version} vs {compareResult.flow_b.name} v{compareResult.flow_b.version}
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                    <div><strong>A:</strong> {compareResult.flow_a.step_count} étapes</div>
                    <div><strong>B:</strong> {compareResult.flow_b.step_count} étapes</div>
                  </div>
                  {compareResult.differences.added_steps?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: '#22c55e', fontWeight: 600 }}>+ Étapes ajoutées dans B ({compareResult.differences.added_steps.length})</div>
                      {compareResult.differences.added_steps.map((s, i) => <div key={i} style={{ paddingLeft: 12, color: '#22c55e' }}>• {s.step_name || s.screen_name}</div>)}
                    </div>
                  )}
                  {compareResult.differences.removed_steps?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: '#ef4444', fontWeight: 600 }}>− Étapes supprimées dans B ({compareResult.differences.removed_steps.length})</div>
                      {compareResult.differences.removed_steps.map((s, i) => <div key={i} style={{ paddingLeft: 12, color: '#ef4444' }}>• {s.step_name || s.screen_name}</div>)}
                    </div>
                  )}
                  {compareResult.differences.modified_steps?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: '#f59e0b', fontWeight: 600 }}>~ Étapes modifiées ({compareResult.differences.modified_steps.length})</div>
                      {compareResult.differences.modified_steps.map((s, i) => (
                        <div key={i} style={{ paddingLeft: 12, color: '#f59e0b' }}>
                          • {s.step_name || s.screen_name}: {s.changes?.join(', ')}
                        </div>
                      ))}
                    </div>
                  )}
                  {compareResult.differences.selector_changes && (
                    <div>
                      <div style={{ color: '#8b5cf6', fontWeight: 600 }}>Sélecteurs: +{compareResult.differences.selector_changes.added || 0} / −{compareResult.differences.selector_changes.removed || 0} / ~{compareResult.differences.selector_changes.modified || 0}</div>
                    </div>
                  )}
                </div>
              )}
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
}

/* ═══════════════════════════════════════════════
   STEP DETAIL EDITOR (right panel)
   ═══════════════════════════════════════════════ */

function StepDetailEditor({ step, flowType, onUpdate, onDelete, onAddSelector, onUpdateSelector, onDeleteSelector, onUploadScreenshot, onCaptureFromDevice, onRefreshFlow, devices, captureDeviceId, onDeviceChange, capturing }) {
  const [form, setForm] = useState({ ...step });
  const [captureResult, setCaptureResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isLibrary = flowType === 'ui-library';
  const [aiAnalysis, setAiAnalysis] = useState(step.ai_analysis || null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => { setForm({ ...step }); setCaptureResult(null); setAiAnalysis(step.ai_analysis || null); }, [step]);

  const save = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    onUpdate(updated);
  };

  const handleCapture = async () => {
    const result = await onCaptureFromDevice();
    if (result) setCaptureResult(result);
  };

  const handleAiAnalyze = async () => {
    if (analyzing || !captureDeviceId) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`/api/script-flows/steps/${step.id}/ai-analyze`, { device_id: captureDeviceId });
      if (res.data.success) {
        setAiAnalysis(res.data.analysis);
      }
    } catch (err) {
      console.error('AI analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await axios.post(`/api/script-flows/steps/${step.id}/selectors`, {
        selector_type: suggestion.selector_type,
        selector_value: suggestion.selector_value,
        description: suggestion.description || '',
        is_primary: suggestion.is_primary || false,
      });
      // Remove from suggestions
      setAiAnalysis(prev => prev ? { ...prev, suggestions: prev.suggestions.filter(s => s.selector_value !== suggestion.selector_value) } : null);
      // Refresh flow to show new selector
      if (onRefreshFlow) onRefreshFlow();
    } catch (err) { console.error(err); }
  };

  return (
    <DetailBody>
      {/* Screenshot preview + capture button overlay */}
      <div style={{ position: 'relative' }}>
        <StepScreenshot style={{ width: '100%', height: 180, cursor: 'pointer' }} onClick={onUploadScreenshot}>
          {step.screenshot_path ? (
            <img src={step.screenshot_path} alt={step.title} style={{ objectFit: 'contain' }} />
          ) : (
            <div className="placeholder">
              <Upload size={24} />
              <span>Cliquer pour uploader</span>
            </div>
          )}
        </StepScreenshot>
        <Btn style={{ position: 'absolute', bottom: 8, right: 8, background: '#22c55e', color: '#fff', padding: '5px 10px', fontSize: 11, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,.3)' }}
          onClick={handleCapture} disabled={capturing || !captureDeviceId}
          title="Capturer screenshot + XML dump depuis le device">
          {capturing ? <SpinnerIcon><Loader size={12} /></SpinnerIcon> : <Crosshair size={12} />}
          {capturing ? ' Capture...' : ' Capturer'}
        </Btn>
      </div>

      {/* Capture result */}
      {captureResult && (
        <div style={{ fontSize: 11, padding: '8px 10px', borderRadius: 6, background: captureResult.selectors ? '#22c55e12' : '#f59e0b12', border: `1px solid ${captureResult.selectors ? '#22c55e33' : '#f59e0b33'}` }}>
          {captureResult.selectors ? (
            <>
              <div style={{ fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>
                <CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Sélecteurs: {captureResult.selectors.matched}/{captureResult.selectors.total} trouvés
                {captureResult.selectors.missing > 0 && <span style={{ color: '#ef4444' }}> • {captureResult.selectors.missing} manquants</span>}
                {captureResult.auto_added > 0 && <span style={{ color: '#6366f1' }}> • {captureResult.auto_added} auto-ajoutés</span>}
              </div>
              <div style={{ color: 'var(--text-muted)' }}>
                UI: {captureResult.ui_elements?.resource_ids} IDs, {captureResult.ui_elements?.content_descs} descs, {captureResult.ui_elements?.texts} textes
              </div>
              {captureResult.new_resource_ids?.length > 0 && (
                <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>
                  Nouveaux IDs: {captureResult.new_resource_ids.slice(0, 5).join(', ')}
                  {captureResult.new_resource_ids.length > 5 && ` +${captureResult.new_resource_ids.length - 5}`}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: '#f59e0b' }}>Screenshot capturé (UI dump indisponible)</div>
          )}
        </div>
      )}

      {/* Main fields */}
      <FormGroup>
        <label>Titre</label>
        <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} onBlur={() => save('title', form.title)} />
      </FormGroup>

      <FormGroup>
        <label>Description</label>
        <TextArea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} onBlur={() => save('description', form.description)} rows={2} />
      </FormGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormGroup>
          <label>Type d'action</label>
          <Select value={form.action_type || 'action'} onChange={e => save('action_type', e.target.value)}>
            {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <label>Écran</label>
          <Input value={form.screen_name || ''} onChange={e => setForm(f => ({ ...f, screen_name: e.target.value }))} onBlur={() => save('screen_name', form.screen_name)} placeholder="Ex: Feed, Profile..." />
        </FormGroup>
      </div>

      <FormGroup>
        <label>Notes</label>
        <TextArea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} onBlur={() => save('notes', form.notes)} rows={2} placeholder="Notes techniques..." />
      </FormGroup>

      {/* Advanced fields — collapsible (hidden for UI Library) */}
      {!isLibrary && (
      <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}
          onClick={() => setShowAdvanced(!showAdvanced)}>
          <ChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
          Avancé
        </div>
        {showAdvanced && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FormGroup>
                <label>Ordre</label>
                <Input type="number" value={form.step_order || ''} onChange={e => setForm(f => ({ ...f, step_order: parseInt(e.target.value) }))} onBlur={() => save('step_order', form.step_order)} />
              </FormGroup>
              <FormGroup>
                <label>Statut</label>
                <Select value={form.status || 'active'} onChange={e => save('status', e.target.value)}>
                  <option value="active">Actif</option>
                  <option value="deprecated">Déprécié</option>
                  <option value="draft">Brouillon</option>
                </Select>
              </FormGroup>
            </div>
            <FormGroup>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_conditional || false} onChange={e => save('is_conditional', e.target.checked)} />
                Étape conditionnelle
              </label>
              {form.is_conditional && (
                <Input value={form.condition_label || ''} onChange={e => setForm(f => ({ ...f, condition_label: e.target.value }))} onBlur={() => save('condition_label', form.condition_label)} placeholder="Ex: Si le compte est privé..." />
              )}
            </FormGroup>
            <FormGroup>
              <label>Branche</label>
              <Input value={form.branch_label || ''} onChange={e => setForm(f => ({ ...f, branch_label: e.target.value }))} onBlur={() => save('branch_label', form.branch_label)} placeholder="Ex: Chemin alternatif..." />
            </FormGroup>
          </div>
        )}
      </div>
      )}

      {/* ─── Selectors ─── */}
      <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Crosshair size={14} color="var(--primary)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Sélecteurs ({step.selectors?.length || 0})</span>
          <Btn style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: 11 }}
            onClick={handleAiAnalyze} disabled={analyzing || !captureDeviceId}
            title="Analyser le XML dump avec l'IA pour suggérer des sélecteurs">
            {analyzing ? <SpinnerIcon><Loader size={12} /></SpinnerIcon> : <Sparkles size={12} />}
          </Btn>
          <Btn style={{ padding: '3px 8px', fontSize: 11 }} onClick={onAddSelector}><Plus size={12} /></Btn>
        </div>

        {/* AI suggestions panel */}
        {aiAnalysis?.suggestions?.length > 0 && (
          <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'linear-gradient(135deg, #6366f108, #a855f708)', border: '1px solid #6366f133', fontSize: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 600, color: '#a855f7' }}>
              <Sparkles size={12} /> Suggestions IA {aiAnalysis.screen_detected && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>• {aiAnalysis.screen_detected}</span>}
            </div>
            {aiAnalysis.suggestions.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderTop: i > 0 ? '1px solid var(--card-border)' : 'none' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={`[${s.selector_type}] ${s.selector_value}\n${s.reason || ''}`}>
                  [{s.selector_type}] {s.selector_value}
                </span>
                <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3,
                  background: s.confidence === 'haute' ? '#22c55e22' : s.confidence === 'moyenne' ? '#f59e0b22' : '#64748b22',
                  color: s.confidence === 'haute' ? '#22c55e' : s.confidence === 'moyenne' ? '#f59e0b' : '#64748b',
                }}>{s.confidence}</span>
                <Btn style={{ padding: '2px 6px', fontSize: 10, background: '#22c55e', color: '#fff' }}
                  onClick={() => handleAcceptSuggestion(s)} title={s.reason || 'Ajouter ce sélecteur'}>
                  <Plus size={10} />
                </Btn>
              </div>
            ))}
            {aiAnalysis.notes && <div style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 10, fontStyle: 'italic' }}>{aiAnalysis.notes}</div>}
          </div>
        )}

        {step.selectors?.map(sel => (
          <SelectorEditor key={sel.id} selector={sel} onUpdate={(data) => onUpdateSelector(sel.id, data)} onDelete={() => onDeleteSelector(sel.id)} />
        ))}
      </div>

      {/* Delete step */}
      <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 16, marginTop: 8 }}>
        <Btn $danger style={{ width: '100%', justifyContent: 'center' }} onClick={onDelete}>
          <Trash2 size={14} /> Supprimer cette étape
        </Btn>
      </div>
    </DetailBody>
  );
}

/* ═══════════════════════════════════════════════
   SELECTOR EDITOR
   ═══════════════════════════════════════════════ */

function SelectorEditor({ selector, onUpdate, onDelete }) {
  const [form, setForm] = useState({ ...selector });
  useEffect(() => { setForm({ ...selector }); }, [selector]);

  const save = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    onUpdate(updated);
  };

  return (
    <SelectorCard>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Select style={{ flex: 1, fontSize: 11, padding: '4px 8px' }} value={form.selector_type} onChange={e => save('selector_type', e.target.value)}>
          {SELECTOR_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_primary || false} onChange={e => save('is_primary', e.target.checked)} />
          Principal
        </label>
        <Btn style={{ padding: 2, background: 'transparent', color: '#ef4444' }} onClick={onDelete}><Trash2 size={12} /></Btn>
      </div>
      <Input style={{ width: '100%', fontSize: 11, fontFamily: 'monospace', padding: '5px 8px', marginBottom: 4 }}
        value={form.selector_value || ''} placeholder="Valeur du sélecteur..."
        onChange={e => setForm(f => ({ ...f, selector_value: e.target.value }))} onBlur={() => save('selector_value', form.selector_value)} />
      <Input style={{ width: '100%', fontSize: 11, padding: '5px 8px', marginBottom: 4 }}
        value={form.description || ''} placeholder="Description..."
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} onBlur={() => save('description', form.description)} />
      <Input style={{ width: '100%', fontSize: 11, fontFamily: 'monospace', padding: '5px 8px', opacity: .7 }}
        value={form.fallback_value || ''} placeholder="Fallback (optionnel)..."
        onChange={e => setForm(f => ({ ...f, fallback_value: e.target.value }))} onBlur={() => save('fallback_value', form.fallback_value)} />
    </SelectorCard>
  );
}

/* ═══════════════════════════════════════════════
   FLOW CREATE/EDIT MODAL
   ═══════════════════════════════════════════════ */

function FlowModal({ initial, onSave, onClose, devices = [], defaultDeviceId = '' }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    script_type: initial?.script_type || 'interact',
    description: initial?.description || '',
    version: initial?.version || '1.0',
    app_version: initial?.app_version || '',
    color: initial?.color || '#6366f1',
    icon: initial?.icon || 'code',
  });
  const [detectDeviceId, setDetectDeviceId] = useState(defaultDeviceId);
  const [detecting, setDetecting] = useState(false);
  const [targetApp, setTargetApp] = useState(initial?.app_version?.split(' ')[0] || 'instagram');

  const PACKAGE_MAP = {
    instagram: 'com.instagram.android',
    twitter: 'com.twitter.android',
    gmail: 'com.google.android.gm',
    google: 'com.google.android.googlequicksearchbox',
    tiktok: 'com.zhiliaoapp.musically',
  };

  const TARGET_APPS = [
    { value: 'instagram', label: 'Instagram', color: '#ec4899' },
    { value: 'twitter', label: 'Twitter / X', color: '#1d9bf0' },
    { value: 'gmail', label: 'Gmail', color: '#ea4335' },
    { value: 'google', label: 'Google', color: '#4285f4' },
    { value: 'tiktok', label: 'TikTok', color: '#000000' },
    { value: 'other', label: 'Autre', color: '#64748b' },
  ];

  const getAppFromType = (scriptType) => {
    if (scriptType === 'ui-library') return targetApp;
    const type = SCRIPT_TYPES.find(t => t.value === scriptType);
    return type?.app || 'other';
  };

  const autoDetectVersion = async (scriptType, deviceId, appOverride) => {
    const app = appOverride || getAppFromType(scriptType);
    const pkg = PACKAGE_MAP[app];
    if (!pkg || !deviceId) return;
    setDetecting(true);
    try {
      const res = await axios.get(`/api/script-flows/detect-app-version?device_id=${deviceId}&package_name=${pkg}`);
      if (res.data.success) {
        setForm(f => ({ ...f, app_version: `${app} ${res.data.version}` }));
      }
    } catch (err) { /* silently ignore if device not reachable */ }
    finally { setDetecting(false); }
  };

  const handleTypeChange = (scriptType) => {
    const type = SCRIPT_TYPES.find(t => t.value === scriptType);
    setForm(f => ({ ...f, script_type: scriptType, color: type?.color || f.color }));
    if (detectDeviceId) autoDetectVersion(scriptType, detectDeviceId);
  };

  const handleTargetAppChange = (app) => {
    setTargetApp(app);
    if (detectDeviceId) autoDetectVersion('ui-library', detectDeviceId, app);
  };

  const handleDeviceChange = (deviceId) => {
    setDetectDeviceId(deviceId);
    if (deviceId && form.script_type) autoDetectVersion(form.script_type, deviceId);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    const type = SCRIPT_TYPES.find(t => t.value === form.script_type);
    onSave({ ...form, color: type?.color || form.color });
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Layers size={20} color="var(--primary)" />
          <h3>{initial ? 'Modifier le flux' : 'Nouveau Script Flow'}</h3>
          <Btn style={{ padding: 4, background: 'transparent' }} onClick={onClose}><X size={18} /></Btn>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <label>Type de script</label>
            <TypeGrid>
              {SCRIPT_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <TypeCard key={type.value} $active={form.script_type === type.value} $color={type.color}
                    onClick={() => handleTypeChange(type.value)}>
                    <Icon size={20} className="icon" />
                    <span className="label">{type.label}</span>
                  </TypeCard>
                );
              })}
            </TypeGrid>
          </FormGroup>
          {form.script_type === 'ui-library' && (
            <FormGroup>
              <label>App cible</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {TARGET_APPS.map(app => (
                  <Btn key={app.value}
                    style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 600,
                      background: targetApp === app.value ? app.color : 'var(--card-bg)',
                      color: targetApp === app.value ? '#fff' : 'var(--text-muted)',
                      border: `1px solid ${targetApp === app.value ? app.color : 'var(--card-border)'}`,
                    }}
                    onClick={() => handleTargetAppChange(app.value)}>
                    {app.label}
                  </Btn>
                ))}
              </div>
            </FormGroup>
          )}
          <FormGroup>
            <label>Nom du flux</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Gmail Signup v2" autoFocus />
          </FormGroup>
          <FormGroup>
            <label>Description</label>
            <TextArea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Décrivez ce flux..." rows={2} />
          </FormGroup>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <FormGroup>
              <label>Version du flux</label>
              <Input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="1.0" />
            </FormGroup>
            <FormGroup>
              <label>Version de l'app {detecting && <span style={{ color: 'var(--primary)', fontSize: 10 }}>(détection...)</span>}</label>
              <div style={{ display: 'flex', gap: 4 }}>
                <Input style={{ flex: 1 }} value={form.app_version} onChange={e => setForm(f => ({ ...f, app_version: e.target.value }))} placeholder="Auto-détectée si device sélectionné" />
              </div>
            </FormGroup>
          </div>
          {devices.length > 0 && (
            <FormGroup>
              <label>Device (pour auto-détection du package)</label>
              <Select value={detectDeviceId} onChange={e => handleDeviceChange(e.target.value)}>
                <option value="">-- Aucun device --</option>
                {devices.map(d => <option key={d.device_id} value={d.device_id}>{d.device_name || d.device_id} ({d.model || d.device_id})</option>)}
              </Select>
              {detectDeviceId && form.app_version && (
                <div style={{ fontSize: 10, color: '#22c55e', marginTop: 4 }}>
                  ✓ Package détecté : {PACKAGE_MAP[getAppFromType(form.script_type)] || '—'} → {form.app_version}
                </div>
              )}
            </FormGroup>
          )}
        </ModalBody>
        <ModalActions>
          <Btn onClick={onClose}>Annuler</Btn>
          <Btn $primary onClick={handleSubmit} disabled={!form.name.trim()}>
            <Save size={14} /> {initial ? 'Enregistrer' : 'Créer'}
          </Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}
